import { useState, useEffect, useCallback, useRef } from 'react';
import { api, isTokenExpired } from './api';
import AppV2 from './v2/AppV2';

// ── Responsive CSS ─────────────────────────────────────────
const CSS = `
  * { box-sizing: border-box; }
  body { margin: 0; padding: 0; background: #f8f7f4; }
  .skl-app { font-family: -apple-system, BlinkMacSystemFont, Arial, sans-serif; background: #f8f7f4; min-height: 100vh; width: 100%; }
  .skl-header { background: #8C1C1C; width: 100%; }
  .skl-header-inner { max-width: 520px; margin: 0 auto; padding: 12px 16px 0; }
  .skl-inner { width: 100%; max-width: 520px; margin: 0 auto; }
  .skl-body { padding: 12px 16px; }
  .skl-title { font-size: 20px; font-weight: 500; color: #fff; font-family: Georgia, serif; }
  .skl-brand { font-size: 10px; color: rgba(255,255,255,0.6); font-family: Georgia, serif; }
  .skl-tabs { display: flex; overflow-x: auto; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
  .skl-tabs::-webkit-scrollbar { display: none; }
  /* Matches skl-route-agent's dashboard.html loading spinner exactly (same
     size/border-width/duration) so the loading state looks the same across
     both apps when switching between them (2026-08-27) - only the color
     differs, since this one needs to read against a dark crimson background
     instead of a light one. */
  @keyframes skl-spin { to { transform: rotate(360deg); } }
  .skl-spinner { width: 18px; height: 18px; border-radius: 50%; animation: skl-spin 0.7s linear infinite; }
  @media (min-width: 600px) {
    .skl-inner { max-width: 680px; } .skl-header-inner { max-width: 680px; padding: 16px 24px 0; }
    .skl-body { padding: 16px 24px; } .skl-title { font-size: 26px; } .skl-brand { font-size: 12px; }
  }
  @media (min-width: 1025px) {
    .skl-inner { max-width: 860px; } .skl-header-inner { max-width: 860px; padding: 20px 32px 0; }
    .skl-body { padding: 20px 32px; } .skl-title { font-size: 30px; } .skl-brand { font-size: 13px; }
  }
`;
if (typeof document !== 'undefined') {
  const el = document.getElementById('skl-css');
  if (!el) { const s = document.createElement('style'); s.id = 'skl-css'; s.textContent = CSS; document.head.appendChild(s); }
}

// ── Tokens ─────────────────────────────────────────────────
const C = { crimson: '#8C1C1C', black: '#111', white: '#fff', tint: '#FDF2F2', border: '#E8D5D5' };
// Dynamic month helpers
const MONTH_NAMES_ID = ['Januari','Februari','Maret','April','Mei','Juni','Juli','Agustus','September','Oktober','November','Desember'];
const toMonthKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
const toMonthLabel = (key) => {
  const [y, m] = key.split('-');
  return `${MONTH_NAMES_ID[parseInt(m)-1]} ${y}`;
};
// n months before `key` ('YYYY-MM'), n=0 returns key itself. Used for the
// "Bulan rit" picker on RequestSheet -- a late-logged trip needs to say
// which month it actually happened in, separate from monthKey (which
// month's plan/cash it's filed under). See ritase_month_key.
const monthKeyMinus = (key, n) => {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 - n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const CURRENT_MONTH_KEY = toMonthKey(new Date());
const ROLES = [{ id: 'director', label: 'Director' }, { id: 'staff', label: 'Staff' }];
// Tab ids, kept in sync with the URL hash (see App()) so a page refresh
// stays on the tab you were on instead of always dropping back to Overview
// — `tab` was pure React state with no URL backing, so reload always reset it.
const TAB_IDS = ['overview', 'planlines', 'requests', 'amendments', 'reopen', 'ritase', 'pl', 'loadplan'];
const tabFromHash = () => {
  const h = window.location.hash.slice(1);
  return TAB_IDS.includes(h) ? h : 'overview';
};
// Stored values are unchanged (load-bearing everywhere: ritase eligibility,
// dashboard Pergi/Balikan/Waste counters, plan-line matching, hundreds of
// historical rows going back to 2020) -- only the LABEL shown in the
// dropdown changed, via TIPE_LABELS below. 2026-09-02 direct feedback:
// "the word balikan invites mistakes" -- for someone new, both a CSK/CAN
// advance and a CSK/CAN invoice could plausibly sound like "Balikan", and
// Lapak's return payment could plausibly sound like "Penerimaan" too.
// 'UJ Balikan' is new (CSK/CAN/Tedco's single whole-trip advance, given
// up front and billed back later -- see server/routes/requests.js's
// autoApprove() comment); 'Balikan' itself is now exclusively Lapak's
// pattern going forward, though the pengeluaran-based bypass in
// autoApprove() stays for historical CSK/CAN rows already tagged
// 'Balikan' from before this redesign.
const TIPE = ['UJ Lampung', 'UJ Waste Lampung', 'UJ Balikan', 'Balikan', 'Penerimaan', 'Operasional', 'Vendor', 'Pinjaman'];
const TIPE_LABELS = {
  'UJ Lampung': 'UJ ke Lampung',
  'UJ Waste Lampung': 'UJ Waste',
  'Balikan': 'Balikan (kalau tidak ada UJ)',
};
// 2026-09-02: CSK folded into CAN as one selectable option (direct
// confirmation -- same real customer group, no longer worth keeping as
// two separate picks). Stored value stays 'CAN' (see CUSTOMER_LABELS
// below for the display-only "CAN (CSK)" text, same label/value split as
// TIPE_LABELS) -- historical rows already tagged customer='CSK' are
// unaffected and still handled by the bypass check in
// server/routes/requests.js. 'Lapak CSK' is new and genuinely distinct
// (a different real pickup point, same receive-only financial pattern as
// Lapak) -- demonstrates the one-line-addition promise from the
// CUSTOMER_TIPE_OPTIONS comment below.
// 2026-10-02: the lapak buyers are their own customers (LSJ = PT Lautan
// Samudera Jayamas in Accurate, Pak Aceng, Pak Ahmad); 'Lapak' stays for
// older lines that name no buyer. See server/services/buyers.js.
const CUSTOMERS = ['LSJ', 'Pak Aceng', 'Pak Ahmad', 'Lapak', 'Lapak CSK', 'CAN', 'Tedco', 'Indahkiat', 'Other'];
// Real names confirmed by the Director 2026-10-02. Stored values stay
// short ('Pak Aceng'), labels say who they are.
// Pak Aceng / Pak Ahmad are invoiced in Accurate as PT Yaputra Abadi Perkasa
// / Wirama Eren Global (server/services/buyers.js ACCURATE_CUSTOMER).
const CUSTOMER_LABELS = { CAN: 'CAN (CSK)', Lapak: 'Lapak (lainnya)', LSJ: 'LSJ (PT Lautan Samudera Jayamas)', 'Pak Aceng': 'Pak Aceng (Lapak Eko · faktur Yaputra)', 'Pak Ahmad': 'Pak Ahmad (UD Barokah · faktur Wirama)' };
// A plan line pre-filling this form can still carry the old standalone
// 'CSK' value (historical data, or an Excel upload from before the
// template merged it into CAN) -- normalize it the same way TIPE_ALIASES
// does for tipe, so the picker never shows a value that isn't in its own
// option list.
const CUSTOMER_ALIASES = { CSK: 'CAN' };
// Which tipe values make sense for a given customer -- narrows the Tipe
// dropdown so a wrong pairing (the actual root cause of the missing-rit
// bugs found 2026-09-01/02) becomes structurally hard to pick, not just
// hard to remember. Adding a future customer with an EXISTING pattern is
// a one-line addition here, no logic changes needed; only a genuinely new
// pattern shape needs real code. 'Other' (and anything not listed) keeps
// the full, unrestricted TIPE list -- the escape hatch for anything
// outside this trip-revenue model (Vendor, Operasional, Pinjaman, etc).
const CUSTOMER_TIPE_OPTIONS = {
  // Lapak trips now carry a UJ paid out per truck (Oct 2026 plan: "UJ
  // Balikan Lapak LSJ 9251"); that UJ is what counts as the trip (rule
  // since 2026-10-02). 'Balikan' stays for receipts from the lapak.
  Lapak: ['UJ Balikan', 'Balikan'],
  'Lapak CSK': ['UJ Balikan', 'Balikan'],
  LSJ: ['UJ Balikan', 'Penerimaan'],
  'Pak Aceng': ['UJ Balikan', 'Penerimaan'],
  'Pak Ahmad': ['UJ Balikan', 'Penerimaan'],
  Indahkiat: ['UJ Lampung', 'UJ Waste Lampung', 'Penerimaan'],
  CAN: ['UJ Balikan', 'Penerimaan'],
  Tedco: ['UJ Balikan', 'Penerimaan'],
};
const tipeOptionsFor = customer => CUSTOMER_TIPE_OPTIONS[customer] || TIPE;
// Which way money moves for a tipe, where that's fixed -- the Tambah
// Pengeluaran / Tambah Penerimaan forms only offer tipes that match their
// direction (2026-09-30: a Penerimaan line added through Tambah
// Pengeluaran booked a 2.85M receipt as money out). 'Balikan' is Lapak's
// receive-only leg going forward (see TIPE above); Pinjaman goes both
// ways, so it's in neither list. Mirrored by hand in
// server/routes/amendments.js.
const INFLOW_TIPE = ['Penerimaan', 'Balikan'];
const OUTFLOW_TIPE = ['UJ Lampung', 'UJ Waste Lampung', 'UJ Balikan', 'Operasional', 'Vendor'];
const addTipeOptionsFor = (customer, addType) => tipeOptionsFor(customer).filter(t =>
  addType === 'add_disbursement' ? !INFLOW_TIPE.includes(t) : !OUTFLOW_TIPE.includes(t));
// Big "which way is this money going" line shown before saving a new line
// and on the Director's approval card, so direction is never a guess.
const DirectionBanner = ({ inflow, amount }) => (
  <div style={{ background: inflow ? '#E1F5EE' : '#FCEBEB', border: `0.5px solid ${inflow ? '#5DCAA5' : '#F09595'}`, borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
    <div style={{ fontSize: 14, fontWeight: 600, color: inflow ? '#085041' : '#A32D2D' }}>
      {inflow ? 'Uang MASUK' : 'Uang KELUAR'} {amount > 0 ? `${inflow ? '+' : '-'}Rp ${fmtFull(amount)}` : ''}
    </div>
  </div>
);
// Shared {value,label} options for every Customer Sel in the app -- so
// "CAN (CSK)" reads consistently wherever a customer is picked, not just
// on the request form.
const CUSTOMER_SELECT_OPTIONS = CUSTOMERS.map(c => ({ value: c, label: CUSTOMER_LABELS[c] || c }));

const fmt = n => {
  if (!n && n !== 0) return '—';
  const a = Math.abs(n);
  if (a >= 1e9) return `${(n/1e9).toFixed(2)}B`;
  if (a >= 1e6) return `${(n/1e6).toFixed(1)}M`;
  if (a >= 1e3) return `${(n/1e3).toFixed(0)}K`;
  return n.toLocaleString('id-ID');
};
const fmtFull = n => n ? n.toLocaleString('id-ID') : '0';
const dateLabel = d => { if (!d) return ''; const dt = new Date(d); return dt.toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }); };
// Groups the "7 hari ke depan" list by day for a collapsible summary
// instead of every line expanded (direct feedback 2026-09-02: "hari ini"
// with every item is fine since it's usually short, but a week's worth
// rendered the same way is crowded). Preserves first-seen date order,
// matching planLines.thisWeek's own ordering.
const groupLinesByDay = lines => {
  const order = [];
  const byDate = {};
  lines.forEach(l => {
    const key = String(l.tanggal || '').slice(0, 10);
    if (!byDate[key]) { byDate[key] = []; order.push(key); }
    byDate[key].push(l);
  });
  return order.map(date => ({ date, lines: byDate[date] }));
};
// One-line digest per day, e.g. "7 UJ, Bayar Indotyre" -- routine
// repeating tipe values (advances, returns) get counted together since
// seeing "7 UJ" says more at a glance than 7 near-identical card titles;
// anything else (Vendor, Operasional, Penerimaan, Pinjaman -- usually
// one-off, individually meaningful payments) gets named directly.
const UJ_TIPE_GROUP = ['UJ Lampung', 'UJ Waste Lampung', 'UJ Balikan'];
const summarizeDayLines = lines => {
  const ujCount = lines.filter(l => UJ_TIPE_GROUP.includes(l.tipe)).length;
  const balikanCount = lines.filter(l => l.tipe === 'Balikan').length;
  const parts = [];
  if (ujCount) parts.push(`${ujCount} UJ`);
  if (balikanCount) parts.push(`${balikanCount} Balikan`);
  lines
    .filter(l => !UJ_TIPE_GROUP.includes(l.tipe) && l.tipe !== 'Balikan')
    .forEach(l => parts.push(l.keterangan));
  return parts.join(', ');
};
// date_change amendments store real ISO dates in original_value/requested_value
// (needed for the dashboard's confirmed-baseline math) - format them for display
// here rather than at write time. Non-date amendment types pass their value through.
// Amendments created before this fix (2026-08-27) still have original_value in
// the old pre-formatted display-string shape (e.g. "27 Agu", not ISO) - running
// that through dateLabel()/new Date() again produces "Invalid Date". Only format
// when it actually looks like an ISO date; otherwise it's already human-readable,
// so show it as-is.
const changeValueLabel = (amendmentType, value) =>
  amendmentType === 'date_change' && /^\d{4}-\d{2}-\d{2}/.test(value || '') ? dateLabel(value) : value;
const today = () => new Date().toISOString().split('T')[0];

const TIER = {
  onplan_ondate:  { bg: '#E1F5EE', color: '#085041', border: '#5DCAA5', label: 'Dalam Plan · Sesuai Tanggal' },
  onplan_offdate: { bg: '#FAEEDA', color: '#633806', border: '#FAC775', label: 'Dalam Plan · Beda Tanggal' },
  exception:      { bg: '#FCEBEB', color: '#A32D2D', border: '#F09595', label: 'Exception' },
};
const AMEND_TYPE = {
  amount_change:       { label: 'Ubah Jumlah',       bg: '#FAEEDA', color: '#633806', border: '#FAC775' },
  date_change:         { label: 'Ubah Tanggal',       bg: '#EEEDFE', color: '#3C3489', border: '#AFA9EC' },
  description_change:  { label: 'Ubah Keterangan',    bg: '#E6F1FB', color: '#0C447C', border: '#85B7EB' },
  customer_change:     { label: 'Ubah Customer',      bg: '#F1EFE8', color: '#5F5E5A', border: '#C4B8B8' },
  tipe_change:         { label: 'Ubah Tipe',           bg: '#FBEAF0', color: '#993556', border: '#ED93B1' },
  cancel_line:         { label: 'Cancel Line',         bg: '#FCEBEB', color: '#A32D2D', border: '#F09595' },
  add_disbursement:    { label: 'Tambah Pengeluaran',  bg: '#FAEEDA', color: '#633806', border: '#FAC775' },
  add_inflow:          { label: 'Tambah Penerimaan',   bg: '#E1F5EE', color: '#085041', border: '#5DCAA5' },
};

// ── Primitives ─────────────────────────────────────────────
const Card = ({ children, style, accent, onClick }) => (
  <div onClick={onClick} style={{ background: C.white, border: accent ? `1.5px solid ${accent}` : `0.5px solid ${C.border}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10, cursor: onClick ? 'pointer' : 'default', ...style }}>{children}</div>
);
const Btn = ({ label, onClick, color = C.crimson, tc = C.white, outline, disabled, full, style }) => (
  <button onClick={onClick} disabled={disabled} style={{ background: outline ? 'transparent' : color, color: outline ? color : tc, border: `1px solid ${color}`, borderRadius: 8, padding: '10px 16px', fontSize: 13, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.5 : 1, width: full ? '100%' : undefined, marginBottom: full ? 8 : 0, ...style }}>{label}</button>
);
const Inp = ({ placeholder, value, onChange, type = 'text', style }) => (
  <input type={type} placeholder={placeholder} value={value || ''} onChange={e => onChange(e.target.value)} style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 10, background: C.white, color: C.black, boxSizing: 'border-box', ...style }} />
);
// options may be plain strings (value === label, every existing call site)
// or {value, label} objects (used by RequestSheet's Tipe picker so the
// stored value can stay stable while the displayed text changes -- see
// TIPE_LABELS).
const Sel = ({ value, onChange, options, placeholder }) => (
  <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 13, marginBottom: 10, background: C.white, color: C.black, boxSizing: 'border-box' }}>
    {placeholder && <option value="">{placeholder}</option>}
    {options.map(o => {
      const v = typeof o === 'object' ? o.value : o;
      const label = typeof o === 'object' ? o.label : o;
      return <option key={v} value={v}>{label}</option>;
    })}
  </select>
);
const Tag = ({ label, bg, color, border }) => (
  <span style={{ fontSize: 10, fontWeight: 500, padding: '2px 8px', borderRadius: 8, background: bg, color, border: `0.5px solid ${border || bg}`, display: 'inline-block' }}>{label}</span>
);
const Sec = ({ children }) => <div style={{ fontSize: 12, fontWeight: 500, color: C.black, margin: '14px 0 8px' }}>{children}</div>;
const Div = () => <div style={{ height: '0.5px', background: C.border, margin: '10px 0' }} />;
const Spin = () => <div style={{ textAlign: 'center', padding: 40, color: '#888', fontSize: 13 }}>Loading...</div>;
const Empty = ({ msg }) => <div style={{ textAlign: 'center', padding: 32, color: '#888', fontSize: 13 }}>{msg}</div>;

// ── Bottom Sheet ───────────────────────────────────────────
const Sheet = ({ title, onClose, children }) => (
  <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', zIndex: 200 }} onClick={e => e.target === e.currentTarget && onClose()}>
    <div style={{ background: C.white, borderRadius: '16px 16px 0 0', padding: '20px 18px 36px', width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 16, fontWeight: 500, color: C.black }}>{title}</div>
        <button onClick={onClose} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#888' }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

// ── Plan Line Card ─────────────────────────────────────────
// Used on dashboard and plan lines view
// role determines what actions show
const PlanLineCard = ({ line, role, onRequest, onAmend, onReverse, compact, pendingAmendments, rejectedCancel }) => {
  const [expanded, setExpanded] = useState(false);
  const isPengeluaran = line.pengeluaran > 0;
  const isBalikan = line.tipe === 'Balikan' || line.tipe === 'UJ Balikan';
  const isWaste = line.tipe === 'UJ Waste Lampung';
  const pending = pendingAmendments || [];
  const hasPending = pending.length > 0;
  // Multiple amendments can stack on the same line — actions stay open the
  // whole time so staff can keep amending/requesting on top.
  const canAct = line.status === 'upcoming';
  const isCancelled = line.status === 'cancelled';
  const isPendingCancel = pending.some(a => a.amendment_type === 'cancel_line');
  const isPendingNew = pending.some(a => ['add_line', 'add_disbursement', 'add_inflow'].includes(a.amendment_type));
  const struckOut = isCancelled || isPendingCancel;
  const pendingLabel = isPendingCancel
    ? `⏳ Menunggu approval penghapusan${pending.length > 1 ? ` (+${pending.length - 1} lainnya)` : ''}`
    : isPendingNew && pending.length === 1
    ? '⏳ Item baru — menunggu approval'
    : `⏳ ${pending.length} perubahan menunggu approval`;

  return (
    <Card style={{
      marginBottom: compact ? 6 : 10,
      opacity: isCancelled ? 0.4 : hasPending ? 0.55 : 1,
      textDecoration: struckOut ? 'line-through' : 'none',
      background: hasPending ? '#F5F4F0' : C.white,
      border: hasPending ? `0.5px dashed ${C.border}` : undefined,
    }} onClick={() => !isCancelled && setExpanded(!expanded)}>
      {hasPending && (
        <div style={{ marginBottom: 6 }}>
          <Tag label={pendingLabel} bg="#EEEDFE" color="#3C3489" border="#AFA9EC" />
        </div>
      )}
      {rejectedCancel && !hasPending && (
        <div style={{ marginBottom: 6 }}>
          <Tag label="↩ Pembatalan ditolak Direktur" bg="#FCEBEB" color="#A32D2D" border="#F09595" />
          {rejectedCancel.rejection_note && (
            <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 4 }}>{rejectedCancel.rejection_note}</div>
          )}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div style={{ flex: 1, marginRight: 10 }}>
          <div style={{ display: 'flex', gap: 6, marginBottom: 4, flexWrap: 'wrap', alignItems: 'center' }}>
            <Tag
              label={line.tipe}
              bg={isWaste ? '#FAEEDA' : isBalikan ? '#E1F5EE' : '#E6F1FB'}
              color={isWaste ? '#633806' : isBalikan ? '#085041' : '#0C447C'}
              border={isWaste ? '#FAC775' : isBalikan ? '#5DCAA5' : '#85B7EB'}
            />
            {line.customer && <Tag label={line.customer} bg="#F1EFE8" color="#5F5E5A" border={C.border} />}
            {line.no_truk && <Tag label={`Truk ${line.no_truk}`} bg="#F1EFE8" color="#5F5E5A" border={C.border} />}
          </div>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.black }}>{line.keterangan}</div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{dateLabel(line.tanggal)}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: isPengeluaran ? '#A32D2D' : '#085041' }}>
            {isPengeluaran ? `-Rp ${fmt(line.pengeluaran)}` : `+Rp ${fmt(line.penerimaan)}`}
          </div>
          {line.status === 'disbursed' && line.paid_amount != null && line.paid_amount !== (line.pengeluaran || line.penerimaan || 0) && (
            <div style={{ fontSize: 10, fontWeight: 500, color: '#633806', marginTop: 2 }}>
              Dibayar Rp {fmt(line.paid_amount)} ({line.paid_amount > (line.pengeluaran || line.penerimaan) ? '+' : '-'}{fmt(Math.abs(line.paid_amount - (line.pengeluaran || line.penerimaan)))})
            </div>
          )}
          <div style={{ fontSize: 10, fontWeight: 500, marginTop: 2, color: line.status === 'disbursed' ? '#085041' : line.status === 'cancelled' ? '#888' : hasPending ? '#3C3489' : '#633806' }}>
            {line.status === 'disbursed' ? '✓ Done' : line.status === 'cancelled' ? 'Cancelled' : hasPending ? 'Transitory' : 'Upcoming'}
          </div>
          <div style={{ fontSize: 10, color: '#B4B2A9', marginTop: 2 }}>{expanded ? '▲' : '▼'}</div>
        </div>
      </div>

      {expanded && canAct && (
        <>
          <Div />
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <Btn label="Amend" onClick={e => { e.stopPropagation(); onAmend(line); }} outline color="#3C3489" style={{ flex: 1, padding: '8px 0', fontSize: 12 }} />
            <Btn label="Request →" onClick={e => { e.stopPropagation(); onRequest(line); }} style={{ flex: 2, padding: '8px 0', fontSize: 12 }} />
          </div>
        </>
      )}

      {expanded && !canAct && (
        <>
          <Div />
          {line.status === 'disbursed' && role === 'director' && onReverse ? (
            <Btn label="↩ Batal Pembayaran" onClick={e => { e.stopPropagation(); onReverse(line); }}
              outline color="#A32D2D" style={{ width: '100%', padding: '8px 0', fontSize: 12 }} />
          ) : (
            <div style={{ fontSize: 11, color: '#888', textAlign: 'center' }}>
              {line.status === 'disbursed' ? 'Sudah dibayar' : 'Line dibatalkan'}
            </div>
          )}
        </>
      )}
    </Card>
  );
};

// ── Pending cancels, staff view ────────────────────────────
// Staff often cancel a batch of lines at once, and every one of them stayed
// in the list struck-out until the Director approved -- a day with 6 pending
// cancels read as 19 items when only 13 were still real (direct feedback
// 2026-09-30). Staff lists now leave pending cancels out and park them in one
// collapsed tray at the bottom instead. Director view is unchanged: the
// struck-out cards are useful context while reviewing the approval queue.
const pendingAmendmentsFor = (line, pendingById) =>
  (line.pending_amendment_ids || []).map(id => pendingById[id]).filter(Boolean);
const isPendingCancelLine = (line, pendingById) =>
  pendingAmendmentsFor(line, pendingById).some(a => a.amendment_type === 'cancel_line');
// plan_line_id -> the rejected amendment, for lines whose most recent
// cancel_line was rejected. rejectById only removes the pending flag, so
// without this the line would just silently reappear in the staff list.
// Stays until the line is paid or cancelled again (there's no rejected_at
// to age it out by).
const rejectedCancelsByLine = amendments => {
  const latest = {};
  (amendments || [])
    .filter(a => a.amendment_type === 'cancel_line' && a.plan_line_id)
    .forEach(a => {
      const cur = latest[a.plan_line_id];
      if (!cur || String(a.created_at) > String(cur.created_at)) latest[a.plan_line_id] = a;
    });
  return Object.fromEntries(Object.entries(latest).filter(([, a]) => a.status === 'rejected'));
};
// Splits a line list for the staff view. Everyone else gets it back as-is.
const splitPendingCancels = (lines, role, pendingById) => {
  if (role !== 'staff') return { shown: lines, cancelling: [] };
  return {
    shown: lines.filter(l => !isPendingCancelLine(l, pendingById)),
    cancelling: lines.filter(l => isPendingCancelLine(l, pendingById)),
  };
};

const CancellingTray = ({ lines, role, pendingById, onRequest, onAmend, onReverse }) => {
  const [open, setOpen] = useState(false);
  if (!lines.length) return null;
  const out = lines.reduce((s, l) => s + (l.pengeluaran || 0), 0);
  const inn = lines.reduce((s, l) => s + (l.penerimaan || 0), 0);
  const totals = [out && `-Rp ${fmt(out)}`, inn && `+Rp ${fmt(inn)}`].filter(Boolean).join(', ');
  return (
    <div style={{ marginTop: 10, marginBottom: 10, border: '0.5px dashed #AFA9EC', borderRadius: 12, background: '#F5F4F0', overflow: 'hidden' }}>
      <div onClick={() => setOpen(!open)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, padding: '11px 14px', cursor: 'pointer' }}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#3C3489' }}>⏳ {lines.length} dibatalkan · menunggu approval</div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>Tidak dihitung di daftar atas · {totals}</div>
        </div>
        <div style={{ fontSize: 11, color: '#888' }}>{open ? '▲' : '▼'}</div>
      </div>
      {open && (
        <div style={{ borderTop: `0.5px solid ${C.border}`, padding: '10px 10px 4px' }}>
          {lines.map(l => (
            <PlanLineCard key={l.id} line={l} role={role} onRequest={onRequest} onAmend={onAmend} onReverse={onReverse} compact pendingAmendments={pendingAmendmentsFor(l, pendingById)} />
          ))}
        </div>
      )}
    </div>
  );
};

// ══════════════════════════════════════════════════════════
// CLOSE MONTH BUTTON
// ══════════════════════════════════════════════════════════
function CloseMonthButton({ plan, onRefresh, compact }) {
  const [checking, setChecking] = useState(false);
  const [closing, setClosing] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const check = async () => {
    setChecking(true);
    try {
      const result = await api.closeCheck(plan.id);
      setCheckResult(result);
      setShowConfirm(true);
    } catch (e) { alert(e.message); }
    setChecking(false);
  };

  const close = async () => {
    setClosing(true);
    try {
      await api.closePlan(plan.id, 'Director');
      setShowConfirm(false);
      onRefresh();
    } catch (e) { alert(e.message); }
    setClosing(false);
  };

  return (
    <>
      <button onClick={check} disabled={checking} style={{
        fontSize: 11, fontWeight: 500, padding: compact ? '3px 10px' : '9px 16px',
        border: `0.5px solid #888`, borderRadius: compact ? 8 : 8,
        background: 'transparent', color: '#888', cursor: checking ? 'not-allowed' : 'pointer',
        opacity: checking ? 0.6 : 1, whiteSpace: 'nowrap',
      }}>
        {checking ? 'Memeriksa...' : '🔒 Tutup bulan'}
      </button>

      {showConfirm && (
        <Sheet title={`Tutup ${plan.month}?`} onClose={() => setShowConfirm(false)}>
          {checkResult?.warnings?.length > 0 ? (
            <>
              <div style={{ background: '#FAEEDA', border: '0.5px solid #FAC775', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
                <div style={{ fontSize: 12, fontWeight: 500, color: '#633806', marginBottom: 6 }}>⚠ Peringatan sebelum menutup:</div>
                {checkResult.warnings.map((w, i) => (
                  <div key={i} style={{ fontSize: 12, color: '#633806' }}>• {w}</div>
                ))}
              </div>
              <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>Kamu masih bisa menutup bulan ini, tapi item di atas belum selesai.</div>
            </>
          ) : (
            <div style={{ background: '#E1F5EE', border: '0.5px solid #5DCAA5', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#085041' }}>✓ Semua clear — siap ditutup</div>
            </div>
          )}
          <div style={{ fontSize: 12, color: '#888', marginBottom: 14 }}>
            Setelah ditutup, staff tidak bisa submit request atau amendment baru untuk bulan ini.
          </div>
          <Btn label={closing ? 'Menutup...' : '🔒 Ya, Tutup Bulan Ini'} onClick={close} disabled={closing} full color="#888" />
          <Btn label="Batal" onClick={() => setShowConfirm(false)} outline full />
        </Sheet>
      )}
    </>
  );
}

// ══════════════════════════════════════════════════════════
// DASHBOARD
// ══════════════════════════════════════════════════════════
function Dashboard({ data, loading, role, onRequest, onAmend, onReverse, onRefresh, onGoToRequests }) {
  const [chartView, setChartView] = useState('chart');
  const [hovered, setHovered] = useState(null);
  const [expandedDays, setExpandedDays] = useState(new Set());
  const toggleDay = date => setExpandedDays(prev => {
    const next = new Set(prev);
    next.has(date) ? next.delete(date) : next.add(date);
    return next;
  });
  if (loading) return <Spin />;
  if (!data) return <Empty msg="Gagal load data" />;
  const { plan, requests, budget, pinjaman, ritase, pl, planLines, arusKas } = data;
  const daily = arusKas?.daily || [];
  const deficitDays = arusKas?.deficitDays || [];
  const pendingAmendmentsById = Object.fromEntries(
    (data.amendments?.items || []).filter(a => a.status === 'pending').map(a => [a.id, a])
  );
  const rejectedCancels = role === 'staff' ? rejectedCancelsByLine(data.amendments?.items) : {};
  const { shown: today_lines, cancelling: today_cancelling } = splitPendingCancels(planLines?.today || [], role, pendingAmendmentsById);
  const { shown: week_lines, cancelling: week_cancelling } = splitPendingCancels(planLines?.thisWeek || [], role, pendingAmendmentsById);

  // Chart dimensions
  const W = 520, H = 180;
  const PAD = { top: 16, right: 12, bottom: 28, left: 56 };
  const cW = W - PAD.left - PAD.right;
  const cH = H - PAD.top - PAD.bottom;
  const todayStr = new Date().toISOString().split('T')[0];
  const daysInMonth = daily.length || 30;

  const allSaldos = daily.flatMap(d => [d.saldo, d.saldoConfirmed]).filter(s => s !== undefined);
  const minS = allSaldos.length ? Math.min(...allSaldos, 0) : 0;
  const maxS = allSaldos.length ? Math.max(...allSaldos, 1) : 1;
  const hasDivergence = arusKas?.hasPendingDivergence ?? daily.some(d => !d.isPast && d.saldoConfirmed !== undefined && d.saldoConfirmed !== d.saldo);
  // Add 10% padding top and bottom
  const paddedMin = minS < 0 ? minS * 1.1 : minS;
  const paddedMax = maxS * 1.1;
  const range = paddedMax - paddedMin || 1;

  const xP = (day) => PAD.left + ((day - 1) / Math.max(daysInMonth - 1, 1)) * cW;
  const yP = (val) => PAD.top + cH - ((val - paddedMin) / range) * cH;
  const zeroY = yP(0);

  return (
    <div>
      {!plan && (
        <Card accent={C.crimson}>
          <div style={{ fontSize: 13, fontWeight: 500, color: C.crimson }}>⚠ Belum ada plan bulan ini</div>
          <div style={{ fontSize: 12, color: C.black, marginTop: 4 }}>Finance Controller perlu upload cashflow Excel dari SharePoint</div>
        </Card>
      )}

      {requests?.flagged > 0 && (
        <Card accent="#FAC775" onClick={onGoToRequests} style={{ cursor: 'pointer' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#633806' }}>
            ⚑ {requests.flagged} request perlu dicek
          </div>
          <div style={{ fontSize: 12, color: C.black, marginTop: 4 }}>
            Truk kosong saat approve — ritase belum tercatat. Klik untuk buka tab Requests.
          </div>
        </Card>
      )}

      {plan && (
        <>
          {/* Plan summary */}
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.black }}>{plan.month} · v{plan.version}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {role === 'director' && plan.status === 'active' && (
                  <CloseMonthButton plan={plan} onRefresh={onRefresh} compact />
                )}
                <Tag label={plan.status === 'active' ? 'Active' : 'Closed'} bg={plan.status === 'active' ? '#E1F5EE' : '#F1EFE8'} color={plan.status === 'active' ? '#085041' : '#888'} />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <div style={{ fontSize: 11, color: '#888' }}>Pengeluaran (Operasional)</div>
              <div style={{ fontSize: 11, fontWeight: 500 }}>{budget.executionPct}% · Rp {fmt(budget.remaining)} remaining</div>
            </div>
            <div style={{ height: 6, background: '#F1EFE8', borderRadius: 3 }}>
              <div style={{ height: '100%', width: `${Math.min(budget.executionPct, 100)}%`, background: C.crimson, borderRadius: 3 }} />
            </div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>{budget.disbursedLineCount} of {budget.lineCount} lines disbursed</div>
            {budget.collections?.planned > 0 && (
              <>
                <div style={{ height: '0.5px', background: C.border, margin: '8px 0' }} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <div style={{ fontSize: 11, color: '#888' }}>Penerimaan (Operasional)</div>
                  <div style={{ fontSize: 11, fontWeight: 500, color: '#085041' }}>{budget.collections.pct}%</div>
                </div>
                <div style={{ height: 6, background: '#E1F5EE', borderRadius: 3, marginBottom: 4 }}>
                  <div style={{ height: '100%', width: `${Math.min(budget.collections.pct, 100)}%`, background: '#085041', borderRadius: 3 }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: 11, color: '#085041' }}>Rp {fmt(budget.collections.received)} diterima</div>
                  <div style={{ fontSize: 11, color: '#888' }}>dari Rp {fmt(budget.collections.planned)} planned</div>
                </div>
              </>
            )}
            <div style={{ fontSize: 9, color: '#B4B2A9', marginTop: 8 }}>Tidak termasuk Pinjaman — lihat kartu di bawah</div>
          </Card>

          {/* Pinjaman — Director-lent buffer money, tracked separately so it
              never quietly reads as real operating cashflow. Planned = what's
              currently on the plan; aktual = what really moved (approved
              requests). The warning banner is the actual point: it's the one
              number that answers "is this month really cashflow positive, or
              did I just lend the business money again." */}
          {(pinjaman?.lineCount > 0 || pinjaman?.actualBorrowed > 0 || pinjaman?.actualRepaid > 0) && (
            <Card accent="#AFA9EC" style={{ background: '#EEEDFE' }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#3C3489', marginBottom: 8 }}>Pinjaman Direktur</div>
              {pinjaman.actualNet > 0 && (
                <div style={{ background: '#EEEDFE', border: '0.5px solid #AFA9EC', borderRadius: 8, padding: '6px 10px', marginBottom: 10, fontSize: 11, color: '#3C3489', fontWeight: 500 }}>
                  ⚠ Rp {fmt(pinjaman.actualNet)} cashflow bulan ini berasal dari pinjaman Direktur, bukan operasional
                </div>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Dipinjam</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#3C3489' }}>Rp {fmt(pinjaman.actualBorrowed)}</div>
                  <div style={{ fontSize: 10, color: '#B4B2A9' }}>planned Rp {fmt(pinjaman.plannedBorrow)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Dikembalikan</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#3C3489' }}>Rp {fmt(pinjaman.actualRepaid)}</div>
                  <div style={{ fontSize: 10, color: '#B4B2A9' }}>planned Rp {fmt(pinjaman.plannedRepay)}</div>
                </div>
              </div>
              <div style={{ height: '0.5px', background: '#DDD8F5', margin: '10px 0' }} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <div style={{ fontSize: 11, color: '#888' }}>Net bulan ini</div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#3C3489' }}>Rp {fmt(pinjaman.actualNet)}</div>
                  <div style={{ fontSize: 10, color: '#B4B2A9' }}>planned Rp {fmt(pinjaman.plannedNet)}</div>
                </div>
              </div>
            </Card>
          )}

          {/* Pending alerts */}
          {requests.pending > 0 && (
            <Card accent="#FAC775" style={{ background: '#FAEEDA' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#633806' }}>{requests.pending} request pending approval</div>
              <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {requests.tierBreakdown.onplan_ondate > 0 && <Tag label={`${requests.tierBreakdown.onplan_ondate} on-plan`} bg="#E1F5EE" color="#085041" border="#5DCAA5" />}
                {requests.tierBreakdown.onplan_offdate > 0 && <Tag label={`${requests.tierBreakdown.onplan_offdate} off-date`} bg="#FAEEDA" color="#633806" border="#FAC775" />}
                {requests.tierBreakdown.exception > 0 && <Tag label={`${requests.tierBreakdown.exception} exception`} bg="#FCEBEB" color="#A32D2D" border="#F09595" />}
              </div>
            </Card>
          )}

          {data.amendments?.pending > 0 && (
            <Card accent="#AFA9EC" style={{ background: '#EEEDFE' }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: '#3C3489' }}>{data.amendments.pending} amendment pending approval</div>
            </Card>
          )}
        </>
      )}

      {/* Arus Kas Chart */}
      {plan && daily.length > 0 && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 500, color: C.black }}>Arus Kas</div>
              <div style={{ fontSize: 10, color: '#888' }}>Aktual s/d hari ini · Confirmed setelahnya</div>
            </div>
            <div style={{ display: 'flex', gap: 4 }}>
              {['chart','table'].map(v => (
                <button key={v} onClick={() => setChartView(v)} style={{ padding: '3px 8px', fontSize: 10, fontWeight: chartView===v?500:400, border: chartView===v?`1.5px solid ${C.crimson}`:`0.5px solid ${C.border}`, borderRadius: 6, background: chartView===v?C.tint:C.white, color: chartView===v?C.crimson:'#888', cursor: 'pointer' }}>
                  {v === 'chart' ? 'Grafik' : 'Tabel'}
                </button>
              ))}
            </div>
          </div>

          {(() => {
            const allDeficitDays = daily.filter(d => d.isNegative);
            if (allDeficitDays.length === 0) return null;
            const worstSaldo = Math.min(...allDeficitDays.map(d => d.saldo));
            return (
              <div style={{ background: '#FCEBEB', border: '0.5px solid #F09595', borderRadius: 8, padding: '6px 10px', marginBottom: 8, fontSize: 11, color: '#A32D2D', fontWeight: 500 }}>
                ⚠ Proyeksi defisit mulai {dateLabel(allDeficitDays[0].date)} · {allDeficitDays.length} hari di bawah nol · terendah Rp {fmt(Math.abs(worstSaldo))}
              </div>
            );
          })()}

          {chartView === 'chart' && (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', position: 'relative' }}>
              <svg width={W} height={H} style={{ display: 'block' }}>

                {/* Subtle grid lines */}
                {[0.25, 0.5, 0.75, 1].map((t, i) => {
                  const val = paddedMin + t * range;
                  const y = yP(val);
                  return (
                    <g key={i}>
                      <line x1={PAD.left} y1={y} x2={W - PAD.right} y2={y} stroke="#EEE8E8" strokeWidth={0.5} />
                      <text x={PAD.left - 4} y={y + 3} textAnchor="end" fontSize={8} fill="#C4B8B8">{fmt(val)}</text>
                    </g>
                  );
                })}

                {/* Zero line — solid, labeled */}
                <line x1={PAD.left} y1={zeroY} x2={W - PAD.right} y2={zeroY} stroke="#BBB" strokeWidth={1} />
                <text x={PAD.left - 4} y={zeroY + 3} textAnchor="end" fontSize={8} fill="#999" fontWeight={600}>0</text>

                {/* Red deficit zone — ONLY below zero */}
                {paddedMin < 0 && (() => {
                  const pathStr = daily.map((d, i) => {
                    const x = xP(d.day);
                    // Below zero: go to actual y; above zero: stay at zeroY
                    const y = d.saldo < 0 ? yP(d.saldo) : zeroY;
                    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                  }).join(' ')
                    + ` L ${xP(daily[daily.length - 1].day)} ${zeroY}`
                    + ` L ${xP(daily[0].day)} ${zeroY} Z`;
                  return <path d={pathStr} fill="#A32D2D" opacity={0.12} />;
                })()}

                {/* Today marker */}
                {(() => {
                  const todayIdx = daily.findIndex(d => d.date === todayStr);
                  if (todayIdx < 0) return null;
                  const tx = xP(todayIdx + 1);
                  return (
                    <g>
                      <line x1={tx} y1={PAD.top} x2={tx} y2={H - PAD.bottom} stroke={C.crimson} strokeWidth={0.8} strokeDasharray="3 2" />
                      <text x={tx + 3} y={PAD.top + 10} fontSize={8} fill={C.crimson} fontWeight={500}>Hari ini</text>
                    </g>
                  );
                })()}

                {/* Past line — solid black */}
                {daily.filter(d => d.isPast).length > 1 && (
                  <polyline
                    points={daily.filter(d => d.isPast).map(d => `${xP(d.day)},${yP(d.saldo)}`).join(' ')}
                    fill="none" stroke="#111" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round"
                  />
                )}

                {/* Future line — dashed, same black, continuing the Aktual line.
                    Plots the confirmed (last Director-approved) value by default -
                    this is the "you can rely on this" trajectory, so it keeps the
                    same color as the historical actuals it extends from. Falls
                    back to the live/pending-inclusive value on days with nothing
                    pending (saldoConfirmed is only computed where it can differ).
                    (Swapped from the live/pending-inclusive value 2026-08-27 -
                    that had it backwards: the less-certain, still-pending-approval
                    figure was drawn in the same color/continuity as real actuals,
                    while the safer approved baseline was the one demoted to a
                    dotted overlay - see the overlay below for the swap's other half.)

                    The join day (last isPast day, included only to visually
                    connect this dashed segment to the solid Aktual line above)
                    always uses d.saldo, matching what the Aktual line just drew
                    for that same day - NOT d.saldoConfirmed. A pending amendment
                    can affect a plan_line dated on-or-before today (its schedule
                    already passed even though the edit is still unapproved), which
                    makes saldoConfirmed diverge from saldo on a technically-past
                    day. Using saldoConfirmed for that shared join day forked the
                    "smooth" black line into two different heights at the exact
                    same day - fixed 2026-08-27, reported as "a disconnect in the
                    smooth black line". Only genuinely future days (!isPast) switch
                    to the confirmed value. */}
                {(() => {
                  const firstFutureIdx = daily.findIndex(d => !d.isPast);
                  const startIdx = firstFutureIdx <= 0 ? 0 : firstFutureIdx - 1;
                  const pts = daily.slice(startIdx);
                  return pts.length > 1 ? (
                    <polyline
                      points={pts.map(d => `${xP(d.day)},${yP(d.isPast ? d.saldo : (d.saldoConfirmed ?? d.saldo))}`).join(' ')}
                      fill="none" stroke="#111" strokeWidth={1.5} strokeDasharray="5 3" strokeLinejoin="round"
                    />
                  ) : null;
                })()}

                {/* Pending overlay — the live/pending-inclusive value, purple dotted.
                    Purple+dotted now correctly marks "not yet real" (still-pending,
                    unapproved edits folded in), rather than marking the approved
                    baseline as the odd one out. Starts one point before the first
                    FUTURE day it diverges from the confirmed line (so the two
                    visibly split apart from there) and runs to month end - no need
                    to retrace where they agree.

                    Restricted to !d.isPast (2026-08-27 fix, alongside the join-day
                    fix above): a pending amendment can make saldoConfirmed diverge
                    from saldo on a still-past day, which used to start this overlay
                    days before the black "future" line even began - the two lines
                    visibly started at different points. Divergence on past days is
                    a real, separate data-quality question (should a pending edit
                    even touch an already-passed schedule date?) - not something
                    this chart should try to resolve by drawing an overlay over the
                    solid Aktual line. */}
                {hasDivergence && (() => {
                  const firstDivergeIdx = daily.findIndex(d => !d.isPast && d.saldoConfirmed !== undefined && d.saldoConfirmed !== d.saldo);
                  if (firstDivergeIdx < 0) return null;
                  const startIdx = firstDivergeIdx <= 0 ? 0 : firstDivergeIdx - 1;
                  const pts = daily.slice(startIdx);
                  return pts.length > 1 ? (
                    <polyline
                      points={pts.map(d => `${xP(d.day)},${yP(d.saldo)}`).join(' ')}
                      fill="none" stroke="#8A84D6" strokeWidth={1.5} strokeDasharray="1.5 2.5" strokeLinejoin="round" strokeLinecap="round"
                    />
                  ) : null;
                })()}

                {/* Data points — grey dashed ring on days with an unconfirmed (pending)
                    amendment (purple is reserved for the pending overlay above).
                    Plotted on d.saldo, not the confirmed value (2026-08-27 fix,
                    two reasons): the past/Aktual line always plots d.saldo, so a
                    dot on saldoConfirmed for a past day with a pending amendment
                    floated visibly off that line; and conceptually a dot marking
                    "this day has a pending change" belongs on the line that
                    actually reflects that change (the pending/proposed overlay),
                    not the confirmed baseline it's a deviation from. */}
                {daily.filter(d => d.hasActivity).map((d, i) => (
                  <g key={i} onMouseEnter={() => setHovered(d)} onMouseLeave={() => setHovered(null)} style={{ cursor: 'pointer' }}>
                    <circle cx={xP(d.day)} cy={yP(d.saldo)} r={hovered?.day === d.day ? 4 : 2.5}
                      fill={d.hasPendingAmendment ? '#F1EFE8' : C.white}
                      stroke={d.hasPendingAmendment ? '#B4B2A9' : '#111'}
                      strokeWidth={1.5}
                      strokeDasharray={d.hasPendingAmendment ? '2 1.5' : undefined}
                    />
                  </g>
                ))}

                {/* Hover tooltip */}
                {hovered && (() => {
                  const diverges = hovered.saldoConfirmed !== undefined && hovered.saldoConfirmed !== hovered.saldo;
                  const mainSaldo = hovered.saldoConfirmed ?? hovered.saldo;
                  const tx = Math.min(xP(hovered.day) + 8, W - 134);
                  const ty = Math.max(yP(mainSaldo) - (diverges ? 62 : 50), PAD.top);
                  const hasDetails = hovered.plannedIn > 0 || hovered.plannedOut > 0;
                  const baseH = hasDetails ? 50 : 30;
                  const h = diverges ? baseH + 12 : baseH;
                  return (
                    <g>
                      <rect x={tx} y={ty} width={126} height={h} rx={5}
                        fill={C.white} stroke="#DDD" strokeWidth={0.5}
                        style={{ filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.1))' }}
                      />
                      <text x={tx + 8} y={ty + 14} fontSize={9} fontWeight={600} fill="#111">{dateLabel(hovered.date)}</text>
                      <text x={tx + 8} y={ty + 25} fontSize={8} fill={mainSaldo < 0 ? '#A32D2D' : '#555'}>Saldo: Rp {fmt(mainSaldo)}</text>
                      {hovered.plannedIn > 0 && <text x={tx + 8} y={ty + 36} fontSize={8} fill="#085041">+{fmt(hovered.plannedIn)} masuk</text>}
                      {hovered.plannedOut > 0 && <text x={tx + 8} y={ty + (hovered.plannedIn > 0 ? 46 : 36)} fontSize={8} fill="#A32D2D">-{fmt(hovered.plannedOut)} keluar</text>}
                      {diverges && <text x={tx + 8} y={ty + baseH + 8} fontSize={8} fill="#8A84D6">Termasuk pending: Rp {fmt(hovered.saldo)}</text>}
                    </g>
                  );
                })()}

                {/* X axis date labels */}
                {daily.filter((d, i) => d.day === 1 || d.day === 10 || d.day === 20 || d.day === daysInMonth).map(d => (
                  <text key={d.day} x={xP(d.day)} y={H - 6} textAnchor="middle" fontSize={8} fill="#B4B2A9">
                    {d.day} {['Jan','Feb','Mar','Apr','Mei','Jun','Jul','Ags','Sep','Okt','Nov','Des'][parseInt(d.date?.split('-')[1]) - 1]}
                  </text>
                ))}

                {/* Y axis label */}
                <text x={10} y={PAD.top + cH / 2} textAnchor="middle" fontSize={8} fill="#C4B8B8"
                  transform={`rotate(-90, 10, ${PAD.top + cH / 2})`}>Saldo (Rp)</text>
              </svg>

              {/* Saldo awal / akhir boxes */}
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <div style={{ background: '#F8F7F4', border: `0.5px solid ${C.border}`, borderRadius: 6, padding: '5px 10px' }}>
                  <div style={{ fontSize: 8, color: '#888' }}>Saldo awal</div>
                  <div style={{ fontSize: 11, fontWeight: 500, color: C.black }}>{fmt(arusKas.saldoAwal)}</div>
                </div>
                {daily.length > 0 && (() => {
                  const lastConfirmed = daily[daily.length-1].saldoConfirmed ?? daily[daily.length-1].saldo;
                  return (
                    <div style={{ background: lastConfirmed < 0 ? '#FCEBEB' : '#F8F7F4', border: `0.5px solid ${lastConfirmed < 0 ? '#F09595' : C.border}`, borderRadius: 6, padding: '5px 10px' }}>
                      <div style={{ fontSize: 8, color: '#888' }}>Saldo akhir (confirmed)</div>
                      <div style={{ fontSize: 11, fontWeight: 500, color: lastConfirmed < 0 ? '#A32D2D' : C.black }}>{fmt(lastConfirmed)}</div>
                    </div>
                  );
                })()}
                {hasDivergence && daily.length > 0 && (
                  <div style={{ background: '#EEEDFE', border: '0.5px solid #AFA9EC', borderRadius: 6, padding: '5px 10px' }}>
                    <div style={{ fontSize: 8, color: '#3C3489' }}>Saldo akhir (proyeksi, termasuk pending)</div>
                    <div style={{ fontSize: 11, fontWeight: 500, color: '#3C3489' }}>{fmt(daily[daily.length-1].saldo)}</div>
                  </div>
                )}
              </div>

              {/* Legend */}
              <div style={{ display: 'flex', gap: 14, marginTop: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width={20} height={8}><line x1={0} y1={4} x2={20} y2={4} stroke="#111" strokeWidth={2} /></svg>
                  <span style={{ fontSize: 9, color: '#888' }}>Aktual</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <svg width={20} height={8}><line x1={0} y1={4} x2={20} y2={4} stroke="#111" strokeWidth={1.5} strokeDasharray="4 2" /></svg>
                  <span style={{ fontSize: 9, color: '#888' }}>Confirmed</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <div style={{ width: 16, height: 10, background: '#A32D2D', opacity: 0.15, borderRadius: 2 }} />
                  <span style={{ fontSize: 9, color: '#888' }}>Zona defisit</span>
                </div>
                {hasDivergence && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    <svg width={20} height={8}><line x1={0} y1={4} x2={20} y2={4} stroke="#8A84D6" strokeWidth={1.5} strokeDasharray="1.5 2.5" /></svg>
                    <span style={{ fontSize: 9, color: '#888' }}>Termasuk pending (belum diapprove)</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {chartView === 'table' && (
            <div style={{ overflowX: 'auto', maxHeight: 240, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: C.tint, position: 'sticky', top: 0 }}>
                    {['Tgl','Keterangan','Masuk','Keluar','Saldo', ...(hasDivergence ? ['Termasuk pending'] : [])].map(h => (
                      <th key={h} style={{ padding: '5px 6px', textAlign: h === 'Tgl' ? 'center' : h === 'Keterangan' ? 'left' : 'right', color: h === 'Termasuk pending' ? '#8A84D6' : '#888', fontWeight: 500, borderBottom: `0.5px solid ${C.border}`, fontSize: 10, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {daily.map((d, i) => {
                    const diverges = d.saldoConfirmed !== undefined && d.saldoConfirmed !== d.saldo;
                    return (
                    <tr key={i} style={{ borderBottom: `0.5px solid ${C.border}`, background: d.hasPendingAmendment ? '#F5F4FC' : d.isNegative ? '#FFF5F5' : d.date === todayStr ? C.tint : 'transparent', opacity: d.isPast ? 1 : 0.75 }}>
                      <td style={{ padding: '4px 6px', textAlign: 'center', fontSize: 10, color: d.date === todayStr ? C.crimson : '#888', fontWeight: d.date === todayStr ? 600 : 400 }}>{d.day}</td>
                      <td style={{ padding: '4px 6px', fontSize: 10, color: C.black, maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {d.label || (d.hasActivity ? '—' : '')}
                        {!d.isPast && d.hasActivity && <span style={{ fontSize: 8, color: '#B4B2A9', marginLeft: 3 }}>plan</span>}
                        {d.hasPendingAmendment && <span style={{ fontSize: 8, color: '#8A84D6', marginLeft: 3 }}>⏳ pending</span>}
                      </td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', color: '#085041', fontSize: 10 }}>{d.plannedIn > 0 ? `+${fmt(d.plannedIn)}` : ''}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', color: '#A32D2D', fontSize: 10 }}>{d.plannedOut > 0 ? `-${fmt(d.plannedOut)}` : ''}</td>
                      <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 500, color: (d.saldoConfirmed ?? d.saldo) < 0 ? '#A32D2D' : C.black, fontSize: 10 }}>{fmt(d.saldoConfirmed ?? d.saldo)}</td>
                      {hasDivergence && (
                        <td style={{ padding: '4px 6px', textAlign: 'right', fontSize: 10, color: diverges ? '#3C3489' : '#B4B2A9' }}>{diverges ? fmt(d.saldo) : '—'}</td>
                      )}
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Ritase */}
      <Card>
        <div style={{ fontSize: 12, fontWeight: 500, color: C.black, marginBottom: 10 }}>Ritase bulan ini</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0 }}>
          {[
            { l: 'Pergi', v: ritase.pergi, p: ritase.planned?.pergi, c: C.crimson },
            { l: 'Balikan', v: ritase.balikan, p: ritase.planned?.balikan, c: C.black },
            { l: '⚠ Waste', v: ritase.waste, p: ritase.planned?.waste, c: ritase.waste > 8 ? '#A32D2D' : '#888' },
          ].map((s, i) => (
            <div key={i} style={{ textAlign: 'center', borderLeft: i > 0 ? `0.5px solid ${C.border}` : 'none', padding: '0 12px' }}>
              <div style={{ fontSize: 24, fontWeight: 500, color: s.c }}>
                {s.v}
                {s.p != null && <span style={{ fontSize: 14, fontWeight: 400, color: '#888' }}> / {s.p}</span>}
              </div>
              {/* Done vs planned for the whole month, from the plan lines */}
              {s.p > 0 && (
                <div style={{ height: 3, background: C.border, borderRadius: 2, margin: '4px 0 5px', overflow: 'hidden' }}>
                  <div style={{ width: `${Math.min(100, (s.v / s.p) * 100)}%`, height: '100%', background: s.c }} />
                </div>
              )}
              <div style={{ fontSize: 10, color: '#888' }}>{s.l}</div>
            </div>
          ))}
        </div>
        {ritase.planned && (
          <div style={{ fontSize: 10, color: '#888', marginTop: 10 }}>Sudah jalan / rencana sebulan penuh (dari plan)</div>
        )}
      </Card>

      {/* Today's plan lines */}
      {today_lines.length > 0 && (
        <>
          <Sec>Hari ini ({today_lines.length} item)</Sec>
          {today_lines.map(l => (
            <PlanLineCard key={l.id} line={l} role={role} onRequest={onRequest} onAmend={onAmend} onReverse={onReverse} compact pendingAmendments={pendingAmendmentsFor(l, pendingAmendmentsById)} rejectedCancel={rejectedCancels[l.id]} />
          ))}
        </>
      )}

      {today_lines.length === 0 && plan && (
        <Card style={{ background: '#F1EFE8', border: 'none' }}>
          <div style={{ fontSize: 12, color: '#888', textAlign: 'center' }}>Tidak ada item terjadwal hari ini</div>
        </Card>
      )}
      <CancellingTray lines={today_cancelling} role={role} pendingById={pendingAmendmentsById} onRequest={onRequest} onAmend={onAmend} onReverse={onReverse} />

      {/* This week -- grouped per day, collapsed by default (direct
          feedback 2026-09-02: every item expanded like "hari ini" gets
          crowded over a 7-day span). */}
      {(week_lines.length > 0 || week_cancelling.length > 0) && (
        <>
          <Sec>7 hari ke depan ({week_lines.length} item)</Sec>
          {groupLinesByDay(week_lines).map(({ date, lines }) => {
            const isOpen = expandedDays.has(date);
            return (
              <Card key={date} style={{ padding: 0, overflow: 'hidden' }}>
                <div
                  onClick={() => toggleDay(date)}
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 14px', cursor: 'pointer' }}
                >
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: C.black }}>
                      {dateLabel(date)} <span style={{ fontSize: 11, fontWeight: 400, color: '#888' }}>· {lines.length} item</span>
                    </div>
                    <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{summarizeDayLines(lines)}</div>
                  </div>
                  <div style={{ fontSize: 11, color: '#888' }}>{isOpen ? '▲' : '▼'}</div>
                </div>
                {isOpen && (
                  <div style={{ background: '#F1EFE8', borderTop: `0.5px solid ${C.border}`, padding: '10px 14px' }}>
                    {lines.map(l => (
                      <PlanLineCard key={l.id} line={l} role={role} onRequest={onRequest} onAmend={onAmend} onReverse={onReverse} compact pendingAmendments={pendingAmendmentsFor(l, pendingAmendmentsById)} rejectedCancel={rejectedCancels[l.id]} />
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
          <CancellingTray lines={week_cancelling} role={role} pendingById={pendingAmendmentsById} onRequest={onRequest} onAmend={onAmend} onReverse={onReverse} />
        </>
      )}

      {/* P&L snapshot */}
      {pl && (
        <>
          <Sec>P&L terakhir — {pl.month}</Sec>
          <Card>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { l: 'Pendapatan', v: pl.pendapatan },
                { l: 'Laba Kotor', v: pl.laba_kotor, s: `${pl.laba_kotor_pct}%` },
                { l: 'Laba Bersih Cash', v: pl.laba_bersih_cash, warn: pl.laba_bersih_cash < 5000000 },
                { l: 'Selisih', v: pl.selisih },
              ].map((s, i) => {
                // C.tint (#FDF2F2) is the brand's crimson-family highlight
                // tint, meant for "this is selected" UI, not "this number is
                // bad" — using it as the default background here made every
                // tile read as a loss warning regardless of sign (flagged
                // live 2026-09-17: Pendapatan and a +15.3% Laba Kotor both
                // sat on the same pale-red tile as a genuinely negative Laba
                // Bersih Cash). Only an actual warning/negative value gets a
                // red tile now; everything else is a plain neutral card.
                const isBad = s.warn || s.v < 0;
                return (
                  <div key={i} style={{ background: isBad ? '#FCEBEB' : C.white, border: `0.5px solid ${isBad ? '#F09595' : C.border}`, borderRadius: 8, padding: '10px 12px' }}>
                    <div style={{ fontSize: 10, color: '#888' }}>{s.l}</div>
                    <div style={{ fontSize: 14, fontWeight: 500, color: isBad ? '#A32D2D' : C.black }}>
                      {s.v < 0 ? `(${fmt(Math.abs(s.v))})` : fmt(s.v)}
                      {s.s && <span style={{ fontSize: 10, color: '#888', marginLeft: 4 }}>{s.s}</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PLAN LINES (full list)
// ══════════════════════════════════════════════════════════
function PlanLines({ data, role, onRequest, onAmend, onAddNew, onReverse }) {
  // Defaults to 'upcoming' (direct feedback 2026-09-02) -- staff opening
  // this tab almost always want "what's left to do," not the full history
  // including everything already disbursed.
  const [filter, setFilter] = useState('upcoming');
  if (!data?.planLines) return <Empty msg="Belum ada plan" />;
  const all = data.planLines.all || [];
  const pendingAmendmentsById = Object.fromEntries(
    (data.amendments?.items || []).filter(a => a.status === 'pending').map(a => [a.id, a])
  );

  const rejectedCancels = role === 'staff' ? rejectedCancelsByLine(data.amendments?.items) : {};

  // Pending cancels only come out of the Upcoming list -- "Semua" is the
  // full-history view and keeps them inline, struck out.
  const byFilter = filter === 'all' ? all
    : filter === 'upcoming' ? all.filter(l => l.status === 'upcoming')
    : all.filter(l => l.status === 'disbursed');
  const { shown: filtered, cancelling } = filter === 'upcoming'
    ? splitPendingCancels(byFilter, role, pendingAmendmentsById)
    : { shown: byFilter, cancelling: [] };

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
        {[['upcoming','Upcoming'], ['all','Semua'], ['disbursed','Done']].map(([v,l]) => (
          <button key={v} onClick={() => setFilter(v)} style={{ padding: '5px 12px', fontSize: 11, fontWeight: filter===v?500:400, border: filter===v?`1.5px solid ${C.crimson}`:`0.5px solid ${C.border}`, borderRadius: 8, background: filter===v?C.tint:C.white, color: filter===v?C.crimson:'#888', cursor:'pointer' }}>{l}</button>
        ))}
      </div>

      {(role === 'staff' || role === 'director') && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
          <Btn label="+ Tambah Pengeluaran" onClick={() => onAddNew('add_disbursement')} outline color={C.crimson} style={{ flex: 1, fontSize: 11, padding: '8px 0' }} />
          <Btn label="+ Tambah Penerimaan" onClick={() => onAddNew('add_inflow')} outline color="#085041" style={{ flex: 1, fontSize: 11, padding: '8px 0' }} />
        </div>
      )}

      {filtered.length === 0 && <Empty msg="Tidak ada item" />}
      {filtered.map(l => (
        <PlanLineCard key={l.id} line={l} role={role} onRequest={onRequest} onAmend={onAmend} onReverse={onReverse} pendingAmendments={pendingAmendmentsFor(l, pendingAmendmentsById)} rejectedCancel={l.status === 'upcoming' ? rejectedCancels[l.id] : undefined} />
      ))}
      <CancellingTray lines={cancelling} role={role} pendingById={pendingAmendmentsById} onRequest={onRequest} onAmend={onAmend} onReverse={onReverse} />
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// REQUESTS (approval queue for director, history for all)
// ══════════════════════════════════════════════════════════
function Requests({ data, role, onRefresh }) {
  const [expanded, setExpanded] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [search, setSearch] = useState('');
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [fixingId, setFixingId] = useState(null);
  const [fixNoTruk, setFixNoTruk] = useState('');
  const [reopeningId, setReopeningId] = useState(null);
  const [reopenAmount, setReopenAmount] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [reversingId, setReversingId] = useState(null);
  const [reverseReqReason, setReverseReqReason] = useState('');
  if (!data) return <Spin />;

  const pending = data.requests.items.filter(r => r.status === 'pending');
  const doneAll = data.requests.items.filter(r => r.status !== 'pending');
  // Riwayat has no default sort from the server (dashboard.js's select('*')
  // has no .order()), and only the first 30 render below -- with a full
  // month's history that silently buries anything past whatever arbitrary
  // order Supabase happened to return. Search/flagged-only exist so an
  // item doesn't have to already be in the first 30 to be found (direct
  // feedback 2026-09-01: 11 flagged requests -- see ops_note -- were
  // invisible this way, and there was no way to search by requester).
  const q = search.trim().toLowerCase();
  const done = doneAll.filter(r => {
    if (flaggedOnly && !r.ops_note) return false;
    if (!q) return true;
    return (r.requested_by || '').toLowerCase().includes(q) || (r.keterangan || '').toLowerCase().includes(q);
  });
  const doneLimit = (q || flaggedOnly) ? 200 : 30;

  const approve = async id => {
    setLoading(true);
    try { await api.approveRequest(id); setExpanded(null); onRefresh(); }
    catch (e) { alert(e.message); }
    setLoading(false);
  };
  const reject = async id => {
    setLoading(true);
    try { await api.rejectRequest(id, rejectNote); setExpanded(null); setRejectNote(''); onRefresh(); }
    catch (e) { alert(e.message); }
    setLoading(false);
  };
  const toggleSelect = id => setSelected(s => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const bulkApprove = async () => {
    if (selected.size === 0) return;
    setLoading(true);
    try {
      const { failed } = await api.bulkApproveRequests([...selected]);
      if (failed?.length) alert(`${failed.length} request gagal di-approve`);
      setSelected(new Set());
      onRefresh();
    } catch (e) { alert(e.message); }
    setLoading(false);
  };
  const fixFlag = async id => {
    if (!fixNoTruk.trim()) { alert('No. truk wajib diisi'); return; }
    setLoading(true);
    try {
      await api.fixFlaggedRequest(id, fixNoTruk.trim());
      setFixingId(null);
      setFixNoTruk('');
      onRefresh();
    } catch (e) { alert(e.message); }
    setLoading(false);
  };
  const submitReopen = async id => {
    const parsed = Number(reopenAmount.replace(/[^0-9]/g, ''));
    if (!parsed || parsed <= 0) { alert('Jumlah baru tidak valid'); return; }
    if (!reopenReason.trim()) { alert('Alasan wajib diisi'); return; }
    setLoading(true);
    try {
      await api.reopenRequest(id, parsed, reopenReason.trim());
      setReopeningId(null);
      setReopenAmount('');
      setReopenReason('');
      alert('Pengajuan buka kembali terkirim, menunggu persetujuan Direktur');
      onRefresh();
    } catch (e) { alert(e.message); }
    setLoading(false);
  };
  const submitReverseRequest = async id => {
    if (!reverseReqReason.trim()) { alert('Alasan wajib diisi'); return; }
    setLoading(true);
    try {
      await api.requestReverse(id, reverseReqReason.trim());
      setReversingId(null);
      setReverseReqReason('');
      alert('Pengajuan pembatalan terkirim, menunggu persetujuan Direktur');
      onRefresh();
    } catch (e) { alert(e.message); }
    setLoading(false);
  };

  return (
    <div>
      {pending.length === 0 && doneAll.length === 0 && <Empty msg="Belum ada requests bulan ini" />}

      {pending.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Sec>Menunggu approval ({pending.length})</Sec>
            {role === 'director' && (
              <button onClick={() => setSelected(selected.size === pending.length ? new Set() : new Set(pending.map(r => r.id)))}
                style={{ border: 'none', background: 'transparent', fontSize: 11, color: C.crimson, cursor: 'pointer', fontWeight: 500 }}>
                {selected.size === pending.length ? 'Batal pilih semua' : 'Pilih semua'}
              </button>
            )}
          </div>
          {role === 'director' && selected.size > 0 && (
            <Card style={{ background: '#EEEDFE', border: '0.5px solid #AFA9EC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#3C3489' }}>{selected.size} dipilih</div>
              <Btn label={loading ? 'Memproses...' : `✓ Approve ${selected.size}`} onClick={bulkApprove} disabled={loading} style={{ padding: '7px 16px', fontSize: 12 }} />
            </Card>
          )}
          {pending.map(r => {
            const tc = TIER[r.tier] || TIER.exception;
            const open = expanded === r.id;
            // Requests sent from a line at a different amount land here as
            // exceptions (classifyForLine in server/routes/requests.js) --
            // show the plan figure too, since approving writes the new
            // amount onto the plan line and the saldo.
            const reqLine = r.plan_line_id ? (data.planLines?.all || []).find(l => l.id === r.plan_line_id) : null;
            const linePlanned = reqLine ? (reqLine.pengeluaran || reqLine.penerimaan || 0) : null;
            const amountDiffers = linePlanned !== null && Math.abs(linePlanned - r.amount) >= 10000;
            return (
              <Card key={r.id} accent={tc.border}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                  {role === 'director' && (
                    <input type="checkbox" checked={selected.has(r.id)} onChange={e => { e.stopPropagation(); toggleSelect(r.id); }}
                      onClick={e => e.stopPropagation()} style={{ marginTop: 4, cursor: 'pointer' }} />
                  )}
                <div onClick={() => setExpanded(open ? null : r.id)} style={{ cursor: 'pointer', flex: 1 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', gap: 6, marginBottom: 5, flexWrap: 'wrap' }}>
                        <Tag label={tc.label} bg={tc.bg} color={tc.color} border={tc.border} />
                        {amountDiffers && <Tag label="Beda jumlah" bg="#FAEEDA" color="#633806" border="#FAC775" />}
                        {r.no_truk && <Tag label={`Truk ${r.no_truk}`} bg="#F1EFE8" color="#5F5E5A" border={C.border} />}
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: C.black }}>{r.keterangan}</div>
                      <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{r.request_date} · {r.requested_by}</div>
                      {amountDiffers && (
                        <div style={{ fontSize: 11, color: '#633806', marginTop: 2 }}>Plan Rp {fmtFull(linePlanned)} → diminta Rp {fmtFull(r.amount)}</div>
                      )}
                    </div>
                    <div style={{ textAlign: 'right', marginLeft: 10 }}>
                      <div style={{ fontSize: 15, fontWeight: 500, color: C.black }}>Rp {fmt(r.amount)}</div>
                      <div style={{ fontSize: 10, color: '#888' }}>{open ? '▲' : '▼'}</div>
                    </div>
                  </div>
                </div>
                </div>
                {open && role === 'director' && (
                  <>
                    <Div />
                    <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
                      {r.tipe} · {r.customer || '—'} · Rp {fmtFull(r.amount)}
                    </div>
                    {r.tier === 'exception' && amountDiffers && (
                      <div style={{ background: '#FAEEDA', border: '0.5px solid #FAC775', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#633806' }}>
                        ⚠ Jumlah beda dari plan: Rp {fmtFull(linePlanned)} → Rp {fmtFull(r.amount)} ({r.amount > linePlanned ? '+' : '-'}Rp {fmtFull(Math.abs(r.amount - linePlanned))}). Kalau di-approve, plan tetap Rp {fmtFull(linePlanned)} dan saldo pakai Rp {fmtFull(r.amount)}.
                      </div>
                    )}
                    {r.tier === 'exception' && !amountDiffers && (
                      <div style={{ background: '#FCEBEB', border: '0.5px solid #F09595', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#A32D2D' }}>
                        ⚠ Tidak ada dalam plan yang disetujui
                      </div>
                    )}
                    {r.tier === 'onplan_offdate' && (
                      <div style={{ background: '#FAEEDA', border: '0.5px solid #FAC775', borderRadius: 8, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#633806' }}>
                        Item dalam plan tapi tanggal berbeda
                      </div>
                    )}
                    <textarea placeholder="Catatan penolakan (opsional)" value={rejectNote} onChange={e => setRejectNote(e.target.value)}
                      style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 10, resize: 'none', height: 60, boxSizing: 'border-box', fontFamily: 'inherit' }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Btn label="✗ Reject" onClick={() => reject(r.id)} color="#A32D2D" disabled={loading} style={{ flex: 1, padding: '9px 0' }} />
                      <Btn label="✓ Approve" onClick={() => approve(r.id)} disabled={loading} style={{ flex: 2, padding: '9px 0' }} />
                    </div>
                  </>
                )}
              </Card>
            );
          })}
        </>
      )}

      {doneAll.length > 0 && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Sec>Riwayat</Sec>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="text"
                placeholder="Cari requester atau keterangan..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ fontSize: 12, padding: '6px 10px', border: `0.5px solid ${C.border}`, borderRadius: 6, minWidth: 200 }}
              />
              <button
                onClick={() => setFlaggedOnly(v => !v)}
                style={{
                  fontSize: 11, fontWeight: 500, padding: '6px 10px', borderRadius: 6, cursor: 'pointer',
                  border: flaggedOnly ? '0.5px solid #FAC775' : `0.5px solid ${C.border}`,
                  background: flaggedOnly ? '#FAEEDA' : 'transparent',
                  color: flaggedOnly ? '#633806' : '#5F5E5A',
                }}
              >
                ⚑ Ditandai saja
              </button>
            </div>
          </div>
          {done.length === 0 && <Empty msg="Tidak ada yang cocok" />}
          {done.slice(0, doneLimit).map(r => (
            <Card key={r.id} style={{ opacity: r.tier === 'exception' ? 1 : 0.8 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', gap: 6, marginBottom: 4, flexWrap: 'wrap' }}>
                    {r.tier === 'onplan_offdate' && (
                      <Tag label="⚠ Off-date" bg="#FAEEDA" color="#633806" border="#FAC775" />
                    )}
                    {r.tier === 'exception' && (
                      <Tag label="Exception" bg="#FCEBEB" color="#A32D2D" border="#F09595" />
                    )}
                    {r.no_truk && <Tag label={`Truk ${r.no_truk}`} bg="#F1EFE8" color="#5F5E5A" border={C.border} />}
                    {r.ritase_month_key && r.ritase_month_key !== r.month_key && (
                      <Tag label={`Rit ${toMonthLabel(r.ritase_month_key)}`} bg="#EEEDFE" color="#3C3489" border="#AFA9EC" />
                    )}
                    {r.ops_note && <Tag label="⚑ Perlu dicek" bg="#FAEEDA" color="#633806" border="#FAC775" />}
                    {/* Paired nightly with the GPS trip this payment settled (skl-route-agent ritase_match) */}
                    {r.gps && (r.gps.label
                      ? <Tag label="✓ GPS" bg="#EAF3DE" color="#27500A" border="#C0DD97" />
                      : <Tag label="Tidak ada trip GPS" bg="#FAEEDA" color="#633806" border="#FAC775" />)}
                  </div>
                  <div style={{ fontSize: 12, color: C.black }}>{r.keterangan}</div>
                  <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{r.request_date} · {r.requested_by}</div>
                  {r.gps?.label && <div style={{ fontSize: 11, color: '#27500A', marginTop: 2 }}>Trip: {r.gps.label}</div>}
                  {r.rejection_note && <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 2 }}>Ditolak: {r.rejection_note}</div>}
                  {r.ops_note && <div style={{ fontSize: 11, color: '#633806', marginTop: 2 }}>⚑ {r.ops_note}</div>}
                  {r.status === 'approved' && RITASE_TIPE.includes(r.tipe) && (
                    fixingId === r.id ? (
                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }} onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          placeholder="No. truk"
                          value={fixNoTruk}
                          onChange={e => setFixNoTruk(e.target.value)}
                          autoFocus
                          style={{ flex: 1, fontSize: 12, padding: '6px 10px', border: `0.5px solid ${C.border}`, borderRadius: 6, boxSizing: 'border-box' }}
                        />
                        <Btn label={loading ? '...' : '✓ Simpan'} onClick={() => fixFlag(r.id)} disabled={loading} style={{ padding: '6px 12px', fontSize: 12 }} />
                        <button
                          onClick={() => { setFixingId(null); setFixNoTruk(''); }}
                          style={{ border: 'none', background: 'transparent', fontSize: 12, color: '#888', cursor: 'pointer' }}
                        >
                          Batal
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); setFixingId(r.id); setFixNoTruk(r.no_truk || ''); }}
                        style={{ marginTop: 6, fontSize: 11, fontWeight: 500, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', border: `0.5px solid ${r.ops_note ? '#FAC775' : C.border}`, background: 'transparent', color: r.ops_note ? '#633806' : '#5F5E5A' }}
                      >
                        {r.no_truk ? 'Ubah no. truk' : 'Tambah no. truk'}
                      </button>
                    )
                  )}
                  {r.status === 'approved' && (
                    reopeningId === r.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }} onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          placeholder={`Jumlah baru (sekarang Rp ${fmt(r.amount)})`}
                          value={reopenAmount}
                          onChange={e => setReopenAmount(e.target.value)}
                          autoFocus
                          style={{ fontSize: 12, padding: '6px 10px', border: `0.5px solid ${C.border}`, borderRadius: 6, boxSizing: 'border-box' }}
                        />
                        <input
                          type="text"
                          placeholder="Alasan buka kembali (wajib)"
                          value={reopenReason}
                          onChange={e => setReopenReason(e.target.value)}
                          style={{ fontSize: 12, padding: '6px 10px', border: `0.5px solid ${C.border}`, borderRadius: 6, boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Btn label={loading ? '...' : 'Kirim untuk persetujuan'} onClick={() => submitReopen(r.id)} disabled={loading} style={{ padding: '6px 12px', fontSize: 12 }} />
                          <button
                            onClick={() => { setReopeningId(null); setReopenAmount(''); setReopenReason(''); }}
                            style={{ border: 'none', background: 'transparent', fontSize: 12, color: '#888', cursor: 'pointer' }}
                          >
                            Batal
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); setReopeningId(r.id); setReopenAmount(''); setReopenReason(''); }}
                        style={{ marginTop: 6, marginLeft: 6, fontSize: 11, fontWeight: 500, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', border: '0.5px solid #F09595', background: 'transparent', color: '#A32D2D' }}
                      >
                        Buka kembali — jumlah salah
                      </button>
                    )
                  )}
                  {r.status === 'approved' && (
                    reversingId === r.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }} onClick={e => e.stopPropagation()}>
                        <input
                          type="text"
                          placeholder="Alasan pembatalan (wajib)"
                          value={reverseReqReason}
                          onChange={e => setReverseReqReason(e.target.value)}
                          autoFocus
                          style={{ fontSize: 12, padding: '6px 10px', border: `0.5px solid ${C.border}`, borderRadius: 6, boxSizing: 'border-box' }}
                        />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <Btn label={loading ? '...' : 'Kirim untuk persetujuan'} onClick={() => submitReverseRequest(r.id)} color="#A32D2D" disabled={loading} style={{ padding: '6px 12px', fontSize: 12 }} />
                          <button
                            onClick={() => { setReversingId(null); setReverseReqReason(''); }}
                            style={{ border: 'none', background: 'transparent', fontSize: 12, color: '#888', cursor: 'pointer' }}
                          >
                            Batal
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={e => { e.stopPropagation(); setReversingId(r.id); setReverseReqReason(''); }}
                        style={{ marginTop: 6, marginLeft: 6, fontSize: 11, fontWeight: 500, padding: '5px 10px', borderRadius: 6, cursor: 'pointer', border: '0.5px solid #F09595', background: 'transparent', color: '#A32D2D' }}
                      >
                        Minta pembatalan — transaksi salah
                      </button>
                    )
                  )}
                </div>
                <div style={{ textAlign: 'right', marginLeft: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>Rp {fmt(r.amount)}</div>
                  <div style={{ fontSize: 11, fontWeight: 500, color: r.status === 'approved' ? '#085041' : '#A32D2D' }}>
                    {r.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// AMENDMENTS
// ══════════════════════════════════════════════════════════
// ── Net change helpers ──────────────────────────────────────
// Every approved amendment's Rp impact, reduced to a single signed
// pengeluaran/penerimaan pair so it can be summed across dozens (or
// hundreds) of amendments without re-deriving amount parsing everywhere.
// amount_change / cancel_line read direction off the joined plan_line
// (stable across a line's life — only the amount changes, not which
// field it lives in); add_disbursement/add_inflow read it straight off
// the amendment's own requested_value JSON, since those never get a
// plan_lines join (see findTouchedLine in amendments.js).
// 2026-08-27: add_disbursement/add_inflow amendments only got plan_line_id
// persisted starting with this date's server fix (confirmAmendment in
// amendments.js) — before that, approving one of these always left
// plan_line_id NULL forever (the only link to the line it created,
// pending_amendment_ids, gets cleared the moment it's approved). That means
// a NULL plan_line_id on an "add" amendment is the normal, expected state
// for the vast majority of already-approved historical rows and can't by
// itself mean "no real line exists" — it would be wrong to zero out money
// for all of them.
//
// The one case where it DOES mean that: a real transaction got resubmitted
// and approved a second time (e.g. the original insert silently failed
// under the old plans_lines_tipe_check bug, or someone just re-submitted by
// mistake) while an equivalent amendment that DOES carry a real
// plan_line_id already accounts for the same money. detectDuplicateAddIds
// finds those specific rows — matching on the resulting line's tipe/amount/
// date, not on plan_line_id alone — and only those are excluded below.
// Three such pairs are known live (2026-08-25/26 Pinjaman entries
// resubmitted 2026-08-27): 12a17357/c94c7e53/d3461162 are the real,
// plan_line_id-linked originals; c99cb057/1c03ab49/ae015576 are the
// orphaned duplicates this excludes. The audit rows themselves are kept —
// they're a real record of what was submitted and approved — this only
// keeps them from double-counting money.
function detectDuplicateAddIds(approvedList) {
  const linkedSignatures = new Set();
  approvedList.forEach(x => {
    if ((x.amendment_type === 'add_disbursement' || x.amendment_type === 'add_inflow') && x.plan_line_id && x.plan_lines) {
      const l = x.plan_lines;
      linkedSignatures.add(`${l.tipe}|${l.pengeluaran || 0}|${l.penerimaan || 0}|${String(l.tanggal || '').slice(0, 10)}`);
    }
  });
  const dupIds = new Set();
  if (linkedSignatures.size === 0) return dupIds;
  approvedList.forEach(x => {
    if ((x.amendment_type === 'add_disbursement' || x.amendment_type === 'add_inflow') && !x.plan_line_id) {
      try {
        const v = JSON.parse(x.requested_value);
        const sig = `${v.tipe}|${v.pengeluaran || 0}|${v.penerimaan || 0}|${String(v.tanggal || '').slice(0, 10)}`;
        if (linkedSignatures.has(sig)) dupIds.add(x.id);
      } catch { /* unparseable requested_value — not a match either way */ }
    }
  });
  return dupIds;
}

function computeAmendmentDelta(a, dupIds) {
  if (dupIds && dupIds.has(a.id)) return { pengeluaran: 0, penerimaan: 0 }; // orphaned duplicate resubmission — see detectDuplicateAddIds
  const line = a.plan_lines;
  if (a.amendment_type === 'amount_change' && line) {
    const orig = parseInt(String(a.original_value).replace(/[^0-9]/g, '')) || 0;
    const reqd = parseInt(String(a.requested_value).replace(/[^0-9]/g, '')) || 0;
    const diff = reqd - orig;
    return line.penerimaan > 0 ? { pengeluaran: 0, penerimaan: diff } : { pengeluaran: diff, penerimaan: 0 };
  }
  if (a.amendment_type === 'cancel_line' && line) {
    return line.penerimaan > 0 ? { pengeluaran: 0, penerimaan: -line.penerimaan } : { pengeluaran: -line.pengeluaran, penerimaan: 0 };
  }
  if (a.amendment_type === 'add_disbursement' || a.amendment_type === 'add_inflow') {
    try {
      const parsed = JSON.parse(a.requested_value);
      return { pengeluaran: parsed.pengeluaran || 0, penerimaan: parsed.penerimaan || 0 };
    } catch { return { pengeluaran: 0, penerimaan: 0 }; }
  }
  return { pengeluaran: 0, penerimaan: 0 }; // date/description/customer changes move nothing
}
const netOf = d => d.penerimaan - d.pengeluaran;
// Prefers the joined plan_line's CURRENT tipe (ground truth — reflects any
// retag done after the amendment was created) over the tipe baked into
// requested_value at creation time (stale the moment someone retags the
// line, e.g. the 6 "Terima/Pengembalian Pinjaman dari Pak Yohanes" entries
// created as add_inflow/add_disbursement typed Penerimaan/Operasional
// before being manually retagged Pinjaman on the line itself). This only
// works when a.plan_lines is actually populated, which for add_disbursement/
// add_inflow amendments requires plan_line_id to be set — true for every
// amount_change/cancel_line amendment from creation, but for "add" types
// only since the 2026-08-27 confirmAmendment fix backfills it on approval
// (see amendments.js). Older approved "add" amendments from before that
// fix still show the stale creation-time tipe here — falling back to it is
// correct in that case, not just a missing-line edge case, since there's
// nothing else to fall back to.
const amendmentTipe = a => {
  if (a.plan_lines?.tipe) return a.plan_lines.tipe;
  try { return JSON.parse(a.requested_value).tipe || 'Lainnya'; } catch { return 'Lainnya'; }
};
const amendmentTitle = a => a.plan_lines?.keterangan
  || (a.amendment_type === 'add_disbursement' ? 'Tambah pengeluaran baru'
    : a.amendment_type === 'add_inflow' ? 'Tambah penerimaan baru'
    : 'Plan amendment');

// ── Net change summary — headline for "what actually changed" ──
// Defaults to everything approved this month (the data already loaded
// for the tab). "Kustom" narrows to an approved_at date range; "Versi"
// narrows to a plan_version_after range — useful once the plan is deep
// into triple-digit versions and a date range no longer maps cleanly to
// "what changed since I last looked."
function NetChangeSummary({ approved }) {
  const [range, setRange] = useState('month');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [fromV, setFromV] = useState('');
  const [toV, setToV] = useState('');
  if (approved.length === 0) return null;

  const maxVersion = Math.max(0, ...approved.map(a => a.plan_version_after || 0));

  const applyVersionPreset = n => {
    setRange('version');
    setFromV(String(Math.max(1, maxVersion - n + 1)));
    setToV(String(maxVersion));
  };

  let filtered = approved;
  if (range === 'custom' && from && to) {
    filtered = approved.filter(a => a.approved_at && a.approved_at.slice(0, 10) >= from && a.approved_at.slice(0, 10) <= to);
  } else if (range === 'version' && fromV && toV) {
    const fv = parseInt(fromV) || 0, tv = parseInt(toV) || 0;
    filtered = approved.filter(a => a.plan_version_after >= fv && a.plan_version_after <= tv);
  }

  // Computed over the full approved set (not `filtered`) — a duplicate's
  // real counterpart may sit outside whatever range/version window is
  // currently selected, so the signature match has to see everything.
  const dupIds = detectDuplicateAddIds(approved);

  const totals = filtered.reduce((acc, a) => {
    const d = computeAmendmentDelta(a, dupIds);
    acc.pengeluaran += d.pengeluaran;
    acc.penerimaan += d.penerimaan;
    return acc;
  }, { pengeluaran: 0, penerimaan: 0 });
  const netSaldo = netOf(totals);

  const byTipe = {};
  filtered.forEach(a => {
    const net = netOf(computeAmendmentDelta(a, dupIds));
    const tp = amendmentTipe(a);
    byTipe[tp] = byTipe[tp] || { count: 0, net: 0 };
    byTipe[tp].count += 1;
    byTipe[tp].net += net;
  });
  // Pinjaman is money moved with the Director, not real operating revenue/
  // expense — pulled out of the regular per-tipe list into its own row so
  // it can't quietly blend into "Penerimaan"/"Operasional" the way a plain
  // sorted list would let it. Always rendered (even at zero) so it stays a
  // visible tracked category, not something that only appears once there
  // happens to be a number attached to it.
  const { Pinjaman: pinjamanEntry, ...operationalTipe } = byTipe;
  const maxAbs = Math.max(1, ...Object.values(operationalTipe).map(v => Math.abs(v.net)));

  const rangeBtn = (v, l) => (
    <button onClick={() => setRange(v)} style={{ fontSize: 11, padding: '5px 12px', borderRadius: 999, border: range === v ? `1.5px solid ${C.crimson}` : `0.5px solid ${C.border}`, background: range === v ? C.tint : C.white, color: range === v ? C.crimson : '#888', cursor: 'pointer' }}>{l}</button>
  );
  const presetBtn = (n, l) => (
    <button onClick={() => applyVersionPreset(n)} style={{ fontSize: 11, padding: '5px 10px', borderRadius: 999, border: `0.5px solid ${C.border}`, background: C.white, color: '#888', cursor: 'pointer' }}>{l}</button>
  );

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        {rangeBtn('month', 'Bulan ini')}
        {rangeBtn('custom', 'Kustom')}
        {rangeBtn('version', 'Versi')}
        {maxVersion > 1 && (
          <>
            {presetBtn(10, '10 versi terakhir')}
            {presetBtn(25, '25 versi terakhir')}
            {presetBtn(50, '50 versi terakhir')}
          </>
        )}
      </div>
      {range === 'custom' && (
        <div style={{ display: 'flex', gap: 8 }}>
          <Inp type="date" placeholder="Dari" value={from} onChange={setFrom} />
          <Inp type="date" placeholder="Sampai" value={to} onChange={setTo} />
        </div>
      )}
      {range === 'version' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <input type="number" placeholder="v dari" value={fromV} onChange={e => setFromV(e.target.value)}
            style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 13, background: C.white, color: C.black, boxSizing: 'border-box' }} />
          <span style={{ fontSize: 13, color: '#B4B2A9' }}>→</span>
          <input type="number" placeholder="v sampai" value={toV} onChange={e => setToV(e.target.value)}
            style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 13, background: C.white, color: C.black, boxSizing: 'border-box' }} />
          <span style={{ fontSize: 11, color: '#B4B2A9', whiteSpace: 'nowrap' }}>maks v{maxVersion}</span>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
        <Card>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Δ Pengeluaran</div>
          <div style={{ fontSize: 20, fontWeight: 500, color: totals.pengeluaran > 0 ? '#A32D2D' : '#085041' }}>{totals.pengeluaran >= 0 ? '+' : '−'}Rp {fmt(Math.abs(totals.pengeluaran))}</div>
        </Card>
        <Card>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Δ Penerimaan</div>
          <div style={{ fontSize: 20, fontWeight: 500, color: totals.penerimaan >= 0 ? '#085041' : '#A32D2D' }}>{totals.penerimaan >= 0 ? '+' : '−'}Rp {fmt(Math.abs(totals.penerimaan))}</div>
        </Card>
        <Card>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Δ Saldo bersih</div>
          <div style={{ fontSize: 20, fontWeight: 500, color: netSaldo >= 0 ? '#085041' : '#A32D2D' }}>{netSaldo >= 0 ? '+' : '−'}Rp {fmt(Math.abs(netSaldo))}</div>
        </Card>
        <Card>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Amendment disetujui</div>
          <div style={{ fontSize: 20, fontWeight: 500, color: C.black }}>{filtered.length}</div>
        </Card>
      </div>
      {filtered.length > 0 && (
        <Card>
          <div style={{ fontSize: 11, fontWeight: 500, color: '#888', marginBottom: 8 }}>Per tipe (operasional)</div>
          {Object.keys(operationalTipe).length === 0 && <div style={{ fontSize: 12, color: '#B4B2A9' }}>Tidak ada perubahan operasional pada periode ini</div>}
          {Object.entries(operationalTipe).sort((a, b) => Math.abs(b[1].net) - Math.abs(a[1].net)).map(([tipe, v]) => (
            <div key={tipe} style={{ padding: '5px 0' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 3 }}>
                <span style={{ color: '#5F5E5A' }}>{tipe} <span style={{ color: '#B4B2A9' }}>({v.count})</span></span>
                <span style={{ fontWeight: 500, color: v.net >= 0 ? '#085041' : '#A32D2D' }}>{v.net >= 0 ? '+' : '−'}Rp {fmt(Math.abs(v.net))}</span>
              </div>
              <div style={{ height: 3, background: '#F1EFE8', borderRadius: 2 }}>
                <div style={{ height: '100%', width: `${(Math.abs(v.net) / maxAbs) * 100}%`, background: v.net >= 0 ? '#085041' : '#A32D2D', borderRadius: 2 }} />
              </div>
            </div>
          ))}
        </Card>
      )}
      {/* Pinjaman — always shown, never folded into the operational list above,
          so "how much of this came from borrowing" is never a number you have
          to go dig for. Same purple treatment the app already uses for the
          "amendment pending approval" banner on Dashboard — not a new color. */}
      <Card accent="#AFA9EC" style={{ background: '#EEEDFE' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div style={{ fontSize: 11, fontWeight: 500, color: '#3C3489' }}>Pinjaman Direktur {pinjamanEntry ? `(${pinjamanEntry.count})` : ''}</div>
          <div style={{ fontSize: 13, fontWeight: 500, color: pinjamanEntry ? (pinjamanEntry.net >= 0 ? '#085041' : '#A32D2D') : '#B4B2A9' }}>
            {pinjamanEntry ? `${pinjamanEntry.net >= 0 ? '+' : '−'}Rp ${fmt(Math.abs(pinjamanEntry.net))}` : 'Tidak ada perubahan'}
          </div>
        </div>
        {/* 2026-08-27: this is a Δ from amendments approved in the selected
            range — not the Pinjaman balance itself, and not guaranteed to
            match Overview's saldo Pinjaman (which reads plan_lines directly
            and isn't scoped to a range of approvals at all). Said explicitly
            so the two numbers are never mistaken for the same question. */}
        <div style={{ fontSize: 10, color: '#3C3489', opacity: 0.7, marginTop: 4 }}>
          Δ dari amendment yang disetujui pada periode ini, bukan saldo Pinjaman — lihat Overview untuk saldo saat ini.
        </div>
      </Card>
    </div>
  );
}

// ── Batch ledger — one row per approval decision, not per version ──
// Bulk-approving 44 amendments in one shot still bumps plan.version 44
// times, but it was one director decision — approval_batch_id (shared
// across every amendment in the same bulk-approve call, server-side)
// is the grouping key so the ledger shows one collapsible row for it
// instead of 44 near-identical ones. A solo approve gets its own
// batch of one. Amendments approved before this shipped have no
// batch id — each falls back to its own single-item group.
function groupApprovedBatches(items) {
  // Signature match needs the whole approved set, same as NetChangeSummary —
  // a duplicate's real counterpart may have been approved in a different
  // batch entirely (that's exactly what happened live: 2026-08-26 vs
  // 2026-08-27). See detectDuplicateAddIds.
  const dupIds = detectDuplicateAddIds(items);
  const map = new Map();
  items.forEach(a => {
    const key = a.approval_batch_id || `single-${a.id}`;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(a);
  });
  return [...map.entries()].map(([key, batchItems]) => {
    const sorted = batchItems.slice().sort((a, b) => new Date(a.approved_at) - new Date(b.approved_at));
    const totals = batchItems.reduce((acc, a) => {
      const d = computeAmendmentDelta(a, dupIds);
      acc.pengeluaran += d.pengeluaran;
      acc.penerimaan += d.penerimaan;
      return acc;
    }, { pengeluaran: 0, penerimaan: 0 });
    return { key, approvedAt: sorted[0]?.approved_at, items: sorted, count: batchItems.length, net: netOf(totals) };
  }).sort((a, b) => new Date(b.approvedAt) - new Date(a.approvedAt));
}

function ApprovedBatchCard({ batch }) {
  const [expanded, setExpanded] = useState(false);
  const atLabel = t => AMEND_TYPE[t]?.label || t;
  const atStyle = t => AMEND_TYPE[t] || AMEND_TYPE.amount_change;
  const isSingle = batch.count === 1;
  // 2026-08-27: this used to flag the WHOLE batch card as "Pinjaman" (purple
  // accent + tag) whenever ANY single item inside it was Pinjaman-tipe — a
  // 28-item batch of ordinary receivable edits read as loan-related just
  // because one unrelated item buried inside it happened to be a loan entry.
  // Only tag/accent the batch when it's actually a Pinjaman-only batch (a
  // solo approve or a batch made up entirely of Pinjaman items); otherwise
  // just note the count so it's still visible without mislabeling the batch.
  const pinjamanCount = batch.items.filter(a => amendmentTipe(a) === 'Pinjaman').length;
  const allPinjaman = pinjamanCount > 0 && pinjamanCount === batch.count;
  return (
    <Card accent={allPinjaman ? '#AFA9EC' : undefined}>
      <div onClick={() => setExpanded(e => !e)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
        <div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 3, flexWrap: 'wrap' }}>
            {isSingle && <Tag label={atLabel(batch.items[0].amendment_type)} bg={atStyle(batch.items[0].amendment_type).bg} color={atStyle(batch.items[0].amendment_type).color} border={atStyle(batch.items[0].amendment_type).border} />}
            {allPinjaman && <Tag label="Pinjaman" bg="#EEEDFE" color="#3C3489" border="#AFA9EC" />}
            {!allPinjaman && pinjamanCount > 0 && <Tag label={`${pinjamanCount} item Pinjaman`} bg="#EEEDFE" color="#3C3489" border="#AFA9EC" />}
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: C.black }}>
            {batch.count > 1 ? `${batch.count} amendment disetujui sekaligus` : amendmentTitle(batch.items[0])}
          </div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{batch.approvedAt ? new Date(batch.approvedAt).toLocaleString('id-ID') : '—'}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: batch.net >= 0 ? '#085041' : '#A32D2D' }}>{batch.net >= 0 ? '+' : '−'}Rp {fmt(Math.abs(batch.net))}</div>
          <div style={{ fontSize: 10, color: '#B4B2A9' }}>{expanded ? 'Sembunyikan' : 'Detail'} ▾</div>
        </div>
      </div>
      {expanded && (
        <div style={{ marginTop: 10, borderTop: `0.5px solid ${C.border}`, paddingTop: 10 }}>
          {batch.items.map(a => (
            <div key={a.id} style={{ marginBottom: 8, paddingBottom: 8, borderBottom: `0.5px solid ${C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <Tag label={atLabel(a.amendment_type)} bg={atStyle(a.amendment_type).bg} color={atStyle(a.amendment_type).color} />
                <div style={{ fontSize: 10, color: '#888' }}>v{a.plan_version_before} → v{a.plan_version_after}</div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 500, color: C.black }}>{amendmentTitle(a)}</div>
              {a.original_value && a.requested_value && (
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                  {a.amendment_type === 'cancel_line' ? `Dibatalkan: ${a.original_value}` : `${changeValueLabel(a.amendment_type, a.original_value)} → ${changeValueLabel(a.amendment_type, a.requested_value)}`}
                </div>
              )}
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{a.reason}</div>
              <div style={{ fontSize: 11, color: '#B4B2A9', marginTop: 2 }}>{a.requested_by}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function Amendments({ data, role, onRefresh }) {
  const [view, setView] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  if (!data) return <Spin />;

  const pending = data.amendments.items.filter(a => a.status === 'pending');
  const approved = data.amendments.items.filter(a => a.status === 'approved');
  const rejected = data.amendments.items.filter(a => a.status === 'rejected');

  // Oldest-first: when several amendments stack on the same line, only the
  // oldest may be approved/rejected — resolving out of order lets a later
  // revert clobber an already-approved change. Mark the rest as blocked.
  const pendingById = Object.fromEntries(pending.map(a => [a.id, a]));
  const blockedIds = new Set();
  (data.planLines?.all || []).forEach(line => {
    const ids = line.pending_amendment_ids || [];
    if (ids.length <= 1) return;
    const stack = ids.map(id => pendingById[id]).filter(Boolean)
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    stack.slice(1).forEach(a => blockedIds.add(a.id));
  });

  const approve = async id => {
    setLoading(true);
    try { await api.approveAmendment(id); onRefresh(); }
    catch (e) { alert(e.message); }
    setLoading(false);
  };
  const reject = async id => {
    setLoading(true);
    try { await api.rejectAmendment(id, ''); onRefresh(); }
    catch (e) { alert(e.message); }
    setLoading(false);
  };
  const toggleSelect = id => setSelected(s => {
    const next = new Set(s);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const bulkApprove = async () => {
    if (selected.size === 0) return;
    setLoading(true);
    try {
      const { failed } = await api.bulkApproveAmendments([...selected]);
      if (failed?.length) alert(`${failed.length} amendment gagal di-approve`);
      setSelected(new Set());
      onRefresh();
    } catch (e) { alert(e.message); }
    setLoading(false);
  };

  const atLabel = t => AMEND_TYPE[t]?.label || t;
  const atStyle = t => AMEND_TYPE[t] || AMEND_TYPE.amount_change;

  return (
    <div>
      <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
        {[['pending',`Pending (${pending.length})`],['approved',`Approved (${approved.length})`],['rejected',`Rejected (${rejected.length})`]].map(([v,l]) => (
          <button key={v} onClick={() => setView(v)} style={{ flex: 1, padding: '7px 4px', fontSize: 11, fontWeight: view===v?500:400, border: view===v?`1.5px solid ${C.crimson}`:`0.5px solid ${C.border}`, borderRadius: 8, background: view===v?C.tint:C.white, color: view===v?C.crimson:'#888', cursor: 'pointer' }}>{l}</button>
        ))}
      </div>

      {view === 'pending' && (
        <>
          {pending.length === 0 && <Empty msg="Tidak ada amendment pending" />}
          {pending.length > 0 && role === 'director' && (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <button onClick={() => setSelected(selected.size === pending.length ? new Set() : new Set(pending.map(a => a.id)))}
                style={{ border: 'none', background: 'transparent', fontSize: 11, color: C.crimson, cursor: 'pointer', fontWeight: 500 }}>
                {selected.size === pending.length ? 'Batal pilih semua' : 'Pilih semua'}
              </button>
            </div>
          )}
          {role === 'director' && selected.size > 0 && (
            <Card style={{ background: '#EEEDFE', border: '0.5px solid #AFA9EC', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: '#3C3489' }}>{selected.size} dipilih</div>
              <Btn label={loading ? 'Memproses...' : `✓ Approve ${selected.size}`} onClick={bulkApprove} disabled={loading} style={{ padding: '7px 16px', fontSize: 12 }} />
            </Card>
          )}
          {pending.map(a => {
            const st = atStyle(a.amendment_type);
            const isBlocked = blockedIds.has(a.id);
            return (
              <Card key={a.id} accent={st.border} style={isBlocked ? { opacity: 0.65 } : undefined}>
                <div style={{ display: 'flex', gap: 6, marginBottom: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                  <Tag label={atLabel(a.amendment_type)} bg={st.bg} color={st.color} border={st.border} />
                  {role === 'director' && !isBlocked && (
                    <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggleSelect(a.id)} style={{ cursor: 'pointer' }} />
                  )}
                </div>
                {isBlocked && (
                  <div style={{ background: '#F1EFE8', borderRadius: 8, padding: '6px 10px', marginBottom: 8, fontSize: 11, color: '#5F5E5A' }}>
                    🔒 Selesaikan amendment yang lebih lama pada line ini dulu (oldest-first)
                  </div>
                )}
                <div style={{ fontSize: 13, fontWeight: 500, color: C.black, marginBottom: 6 }}>
                  {a.plan_lines?.keterangan
                    ? a.plan_lines.keterangan
                    : a.amendment_type === 'add_disbursement' ? 'Tambah pengeluaran baru'
                    : a.amendment_type === 'add_inflow' ? 'Tambah penerimaan baru'
                    : 'Plan amendment'}
                </div>
                {/* Deficit warning from activity log detail */}
                {a.deficitInfo?.hasDeficit && (
                  <div style={{ background: '#FCEBEB', border: '0.5px solid #F09595', borderRadius: 8, padding: '6px 10px', marginBottom: 8, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 12 }}>⚠️</span>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#A32D2D' }}>Defisit</div>
                      <div style={{ fontSize: 11, color: '#A32D2D' }}>Saldo akan minus Rp {fmt(Math.abs(a.deficitInfo.deficitAmount))} pada {dateLabel(a.deficitInfo.deficitDate)}</div>
                    </div>
                  </div>
                )}
                {a.deficitInfo && !a.deficitInfo.hasDeficit && (
                  <div style={{ background: '#E1F5EE', border: '0.5px solid #5DCAA5', borderRadius: 8, padding: '5px 10px', marginBottom: 8, fontSize: 11, fontWeight: 500, color: '#085041' }}>
                    ✓ Aman — tidak menyebabkan defisit
                  </div>
                )}
                {a.original_value && a.requested_value && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <div style={{ background: '#F1EFE8', borderRadius: 8, padding: '6px 10px', flex: 1 }}>
                      <div style={{ fontSize: 9, color: '#888' }}>
                        {a.amendment_type === 'cancel_line' ? 'Item dibatalkan' : 'Semula'}
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 500, color: '#5F5E5A' }}>{changeValueLabel(a.amendment_type, a.original_value)}</div>
                    </div>
                    {a.amendment_type !== 'cancel_line' && (
                      <>
                        <div style={{ fontSize: 18, color: '#B4B2A9' }}>→</div>
                        <div style={{ background: st.bg, border: `0.5px solid ${st.border}`, borderRadius: 8, padding: '6px 10px', flex: 1 }}>
                          <div style={{ fontSize: 9, color: st.color }}>Diminta</div>
                          <div style={{ fontSize: 12, fontWeight: 500, color: st.color }}>{changeValueLabel(a.amendment_type, a.requested_value)}</div>
                        </div>
                      </>
                    )}
                  </div>
                )}
                {a.requested_value && !a.original_value && (
                  <div style={{ background: st.bg, border: `0.5px solid ${st.border}`, borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                    {(() => {
                      // Try to parse JSON for add_disbursement / add_inflow
                      try {
                        const parsed = JSON.parse(a.requested_value);
                        return (
                          <div>
                            <div style={{ fontSize: 12, fontWeight: 500, color: st.color, marginBottom: 4 }}>{parsed.keterangan}</div>
                            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                              {parsed.tipe && <span style={{ fontSize: 11, color: '#5F5E5A' }}>{parsed.tipe}</span>}
                              {parsed.customer && <span style={{ fontSize: 11, color: '#5F5E5A' }}>{parsed.customer}</span>}
                              {parsed.tanggal && <span style={{ fontSize: 11, color: '#5F5E5A' }}>{dateLabel(parsed.tanggal)}</span>}
                            </div>
                            {(parsed.pengeluaran > 0 || parsed.penerimaan > 0) && (
                              <div style={{ marginTop: 6 }}>
                                <DirectionBanner inflow={parsed.penerimaan > 0} amount={parsed.penerimaan > 0 ? parsed.penerimaan : parsed.pengeluaran} />
                              </div>
                            )}
                          </div>
                        );
                      } catch {
                        return <div style={{ fontSize: 12, color: st.color }}>{a.requested_value}</div>;
                      }
                    })()}
                  </div>
                )}
                <div style={{ background: '#F8F7F4', borderRadius: 8, padding: '8px 12px', marginBottom: 8 }}>
                  <div style={{ fontSize: 10, color: '#888', marginBottom: 2 }}>Alasan</div>
                  <div style={{ fontSize: 12, color: C.black }}>{a.reason}</div>
                </div>
                <div style={{ fontSize: 11, color: '#888', marginBottom: role === 'director' ? 10 : 0 }}>
                  {a.requested_by} · {new Date(a.created_at).toLocaleDateString('id-ID')} · Plan v{a.plan_version_before}
                </div>
                {role === 'director' && !isBlocked && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Btn label="✗ Reject" onClick={() => reject(a.id)} color="#A32D2D" disabled={loading} style={{ flex: 1, padding: '9px 0' }} />
                    <Btn label="✓ Approve" onClick={() => approve(a.id)} disabled={loading} style={{ flex: 2, padding: '9px 0' }} />
                  </div>
                )}
              </Card>
            );
          })}
        </>
      )}

      {view === 'approved' && (
        <>
          {approved.length === 0 && <Empty msg="Belum ada amendment approved" />}
          {approved.length > 0 && (
            <>
              <NetChangeSummary approved={approved} />
              {groupApprovedBatches(approved).map(batch => <ApprovedBatchCard key={batch.key} batch={batch} />)}
            </>
          )}
        </>
      )}

      {view === 'rejected' && (
        <>
          {rejected.length === 0 && <Empty msg="Tidak ada amendment rejected" />}
          {rejected.map(a => (
            <Card key={a.id} style={{ opacity: 0.7 }}>
              <Tag label={atLabel(a.amendment_type)} bg={atStyle(a.amendment_type).bg} color={atStyle(a.amendment_type).color} />
              <div style={{ fontSize: 13, fontWeight: 500, color: C.black, marginTop: 6 }}>
                {a.plan_lines?.keterangan || 'Plan amendment'}
              </div>
              <div style={{ fontSize: 12, color: C.black, marginTop: 4 }}>{a.reason}</div>
              {a.rejection_note && <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 4 }}>Ditolak: {a.rejection_note}</div>}
              <div style={{ fontSize: 11, color: '#B4B2A9', marginTop: 4 }}>{a.requested_by}</div>
            </Card>
          ))}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// REOPEN — corrections to an already-approved request: either fixing its
// amount (a "koreksi" reopen) or cancelling it outright (a "pembatalan"
// reverse request). Deliberately separate from Amendments: that tab only
// ever edits a future plan_line before it's disbursed. Both of these edit
// or undo a `requests` row after the money already moved (dashboard.js
// sums requests.amount directly as "actual"), so they share one queue here
// instead of blending into plan-ahead edits, and their own fetch since
// neither is month-scoped like the rest of the dashboard.
// ══════════════════════════════════════════════════════════
function Reopen({ role, onRefresh }) {
  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(false);
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  const load = () => Promise.all([api.listPendingReopens(), api.listPendingReverseRequests()])
    .then(([reopens, reverses]) => {
      const merged = [
        ...reopens.reopenRequests.map(r => ({ ...r, kind: 'reopen' })),
        ...reverses.reverseRequests.map(r => ({ ...r, kind: 'reverse' })),
      ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      setItems(merged);
    })
    .catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  if (items === null) return <Spin />;

  const approve = async item => {
    setLoading(true);
    try {
      if (item.kind === 'reopen') await api.approveReopen(item.id);
      else await api.approveReverseRequest(item.id);
      load(); onRefresh();
    } catch (e) { alert(e.message); }
    setLoading(false);
  };
  const reject = async item => {
    setLoading(true);
    try {
      if (item.kind === 'reopen') await api.rejectReopen(item.id, rejectNote);
      else await api.rejectReverseRequest(item.id, rejectNote);
      setRejectingId(null); setRejectNote(''); load(); onRefresh();
    } catch (e) { alert(e.message); }
    setLoading(false);
  };

  return (
    <div>
      <Sec>Koreksi & pembatalan transaksi ({items.length})</Sec>
      {items.length === 0 && <Empty msg="Tidak ada pengajuan koreksi atau pembatalan" />}
      {items.map(r => (
        <Card key={`${r.kind}-${r.id}`} accent="#F09595">
          <div style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
            {r.kind === 'reopen'
              ? <Tag label="Koreksi Jumlah" bg="#FAEEDA" color="#633806" border="#FAC775" />
              : <Tag label="Pembatalan" bg="#FCEBEB" color="#A32D2D" border="#F09595" />}
          </div>
          <div style={{ fontSize: 12, fontWeight: 500, color: C.black }}>{r.requests?.keterangan || '—'}</div>
          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
            {r.requests?.request_date} · {r.requests?.requested_by} {r.requests?.no_truk ? `· Truk ${r.requests.no_truk}` : ''}
          </div>
          {r.kind === 'reopen' ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 13 }}>
              <span style={{ color: '#888', textDecoration: 'line-through' }}>Rp {fmt(r.old_amount)}</span>
              <span style={{ color: '#888' }}>→</span>
              <span style={{ fontWeight: 500, color: '#A32D2D' }}>Rp {fmt(r.new_amount)}</span>
            </div>
          ) : (
            <div style={{ marginTop: 8, fontSize: 13, fontWeight: 500, color: '#A32D2D' }}>
              Batalkan Rp {fmt(r.requests?.amount)}
            </div>
          )}
          <div style={{ fontSize: 11, color: '#5F5E5A', marginTop: 6 }}>{r.reason}</div>
          <div style={{ fontSize: 11, color: '#B4B2A9', marginTop: 2 }}>Diajukan oleh {r.requested_by}</div>
          {role === 'director' && (
            rejectingId === r.id ? (
              <div style={{ marginTop: 10 }}>
                <textarea placeholder="Catatan penolakan (opsional)" value={rejectNote} onChange={e => setRejectNote(e.target.value)}
                  style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 8, resize: 'none', height: 50, boxSizing: 'border-box', fontFamily: 'inherit' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Btn label="✗ Tolak" onClick={() => reject(r)} color="#A32D2D" disabled={loading} style={{ flex: 1, padding: '8px 0' }} />
                  <button onClick={() => { setRejectingId(null); setRejectNote(''); }} style={{ border: 'none', background: 'transparent', fontSize: 12, color: '#888', cursor: 'pointer' }}>Batal</button>
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                <Btn label="✗ Tolak" onClick={() => setRejectingId(r.id)} color="#A32D2D" disabled={loading} style={{ flex: 1, padding: '8px 0' }} />
                <Btn label="✓ Setujui" onClick={() => approve(r)} disabled={loading} style={{ flex: 2, padding: '8px 0' }} />
              </div>
            )
          )}
        </Card>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// RITASE
// ══════════════════════════════════════════════════════════
function Ritase({ data }) {
  if (!data) return <Spin />;
  const { ritase } = data;
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 12 }}>
        {[
          { l: 'Ritase Pergi', v: ritase.pergi, p: ritase.planned?.pergi, c: C.crimson, sub: 'Lampung bulan ini' },
          { l: 'Ritase Balikan', v: ritase.balikan, p: ritase.planned?.balikan, c: C.black, sub: 'backhaul bulan ini' },
          { l: '⚠ Waste Counter', v: ritase.waste, p: ritase.planned?.waste, c: ritase.waste > 8 ? '#A32D2D' : '#888', sub: 'target < 8' },
        ].map((s, i) => (
          <Card key={i} accent={i === 2 && ritase.waste > 8 ? '#F09595' : undefined}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>{s.l}</div>
            <div style={{ fontSize: 28, fontWeight: 500, color: s.c }}>
              {s.v}
              {s.p != null && <span style={{ fontSize: 16, fontWeight: 400, color: '#888' }}> / {s.p}</span>}
            </div>
            <div style={{ fontSize: 10, color: '#888' }}>{s.sub}</div>
          </Card>
        ))}
      </div>
      {ritase.waste > 8 && (
        <Card accent="#F09595" style={{ background: '#FCEBEB' }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: '#A32D2D' }}>⚠ Waste counter di atas target</div>
          <div style={{ fontSize: 12, color: '#A32D2D', marginTop: 4 }}>Saat ini {ritase.waste} rit · target &lt; 8/bulan</div>
        </Card>
      )}
      <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>Dihitung otomatis dari approved UJ dan Balikan requests{ritase.planned ? ' · angka setelah garis miring = rencana sebulan penuh dari plan' : ''}</div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// P&L
// ══════════════════════════════════════════════════════════
function PLScreen({ data, role, monthKey, onRefresh }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const pl = data?.pl;

  const save = async () => {
    // Every one of these is a real number consumed elsewhere (Laba Kotor %,
    // the low-cash warning threshold on Laba Bersih Cash, the locked record
    // other screens read as fact) -- previously only pendapatan happened to
    // be checked (parseInt(undefined) => NaN was accidentally falsy), so a
    // blank field here silently wrote through as 0 forever once locked.
    // isFinite rather than a truthy check since 0 and negative values
    // (Selisih, Laba Bersih Cash) are legitimate.
    const REQUIRED_PL_FIELDS = [
      ['pendapatan', 'Pendapatan'], ['labaKotor', 'Laba Kotor'], ['bebanOps', 'Beban Operasional'],
      ['labaBersihAccounting', 'Laba Bersih (Accounting)'], ['labaBersihCash', 'Laba Bersih (Cash)'], ['selisih', 'Selisih'],
    ];
    const missing = REQUIRED_PL_FIELDS.find(([key]) => !Number.isFinite(parseInt(form[key])));
    if (missing) return alert(`${missing[1]} wajib diisi dengan angka yang valid`);
    setSaving(true);
    try {
      // Ritase Pergi/Balikan/Waste Counter are NOT sent here (removed
      // 2026-09-16) -- the server computes them itself from the same
      // `ritase` table the Ritase tab already reads (see pl.js), instead
      // of trusting a second, independently-typed copy of numbers the
      // system already knows authoritatively. Confirmed live: Finance was
      // re-typing these by hand into this form even though the Ritase tab
      // right next to it already says "Dihitung otomatis dari approved UJ
      // dan Balikan requests" -- the two copies had no way to ever be
      // checked against each other and could silently drift apart.
      // Laba Kotor % also not sent (removed 2026-09-16) -- it's a pure
      // ratio of Laba Kotor / Pendapatan, both already above; the server
      // computes it instead of trusting a third, separately-typed number
      // that could disagree with the two that actually define it.
      await api.savePL({ monthKey, month: toMonthLabel(monthKey), pendapatan: parseInt(form.pendapatan), labaKotor: parseInt(form.labaKotor), bebanOps: parseInt(form.bebanOps), labaBersihAccounting: parseInt(form.labaBersihAccounting), labaBersihCash: parseInt(form.labaBersihCash), selisih: parseInt(form.selisih), enteredBy: 'Finance Controller' });
      setShowForm(false); onRefresh();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  const lock = async () => {
    if (!window.confirm('Lock P&L bulan ini? Tidak bisa diubah setelah di-lock.')) return;
    setSaving(true);
    try { await api.lockPL(monthKey); onRefresh(); }
    catch (e) { alert(e.message); }
    setSaving(false);
  };

  // Added 2026-09-17 (Yohanes: "i wanna allow them to edit as long as
  // still draft, uneditable only when it's locked") -- the backend
  // (POST /api/pl) already upserts freely and only refuses once
  // locked_at is set, but this screen only ever showed "+ Input P&L"
  // when no entry existed yet (!pl) -- the moment a draft existed, that
  // button vanished and only "Lock bulan ini" remained, so there was no
  // way to actually reach the backend's own edit path through the app.
  // Reuses the exact same form/save() flow as a fresh entry -- save()
  // already calls the same upserting endpoint either way.
  const openEdit = () => {
    setForm({
      pendapatan: String(pl.pendapatan ?? ''),
      labaKotor: String(pl.laba_kotor ?? ''),
      bebanOps: String(pl.beban_ops ?? ''),
      labaBersihAccounting: String(pl.laba_bersih_accounting ?? ''),
      labaBersihCash: String(pl.laba_bersih_cash ?? ''),
      selisih: String(pl.selisih ?? ''),
    });
    setShowForm(true);
  };

  return (
    <div>
      {!pl && <Empty msg="P&L bulan ini belum diinput" />}
      {pl && (
        <>
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>P&L {pl.month}</div>
              <Tag label={pl.locked_at ? '🔒 Locked' : 'Draft'} bg={pl.locked_at ? '#E1F5EE' : '#FAEEDA'} color={pl.locked_at ? '#085041' : '#633806'} />
            </div>
            {[['Pendapatan', pl.pendapatan], ['Laba Kotor', pl.laba_kotor, `${pl.laba_kotor_pct}%`], ['Beban Operasional', pl.beban_ops], ['Laba Bersih (Accounting)', pl.laba_bersih_accounting], ['Laba Bersih (Cash)', pl.laba_bersih_cash, null, pl.laba_bersih_cash < 5000000], ['Selisih Outstanding', pl.selisih]].map(([label, val, suf, warn], i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: `0.5px solid ${C.border}` }}>
                <div style={{ fontSize: 12, color: C.black }}>{label}</div>
                <div style={{ fontSize: 12, fontWeight: 500, color: warn ? '#A32D2D' : val < 0 ? '#A32D2D' : C.black }}>
                  {val < 0 ? `(${fmt(Math.abs(val))})` : fmt(val)}{suf && <span style={{ fontSize: 10, color: '#888', marginLeft: 4 }}>{suf}</span>}
                </div>
              </div>
            ))}
          </Card>
          <Card>
            <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Ritase</div>
            <div style={{ display: 'flex' }}>
              {[['Pergi', pl.ritase_pergi, C.crimson], ['Balikan', pl.ritase_balikan, C.black], ['Waste', pl.waste_counter, pl.waste_counter > 8 ? '#A32D2D' : '#888']].map(([l, v, c], i) => (
                <div key={i} style={{ flex: 1, textAlign: 'center', borderLeft: i > 0 ? `0.5px solid ${C.border}` : 'none' }}>
                  <div style={{ fontSize: 20, fontWeight: 500, color: c }}>{v}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{l}</div>
                </div>
              ))}
            </div>
          </Card>
          {pl.sharepoint_pdf_url && (
            <a href={pl.sharepoint_pdf_url} target="_blank" rel="noreferrer" style={{ display: 'block', background: C.tint, border: `0.5px solid ${C.crimson}`, borderRadius: 12, padding: '12px 14px', marginBottom: 10, fontSize: 13, color: C.crimson, textDecoration: 'none', fontWeight: 500 }}>→ Lihat PDF yang diapprove ↗</a>
          )}
          {(role === 'staff' || role === 'director') && !pl?.locked_at && (
            <>
              <Btn label="✎ Edit P&L" onClick={openEdit} disabled={saving} outline full />
              {role === 'director' && <Btn label="🔒 Lock bulan ini" onClick={lock} disabled={saving} full />}
            </>
          )}
        </>
      )}
      {(role === 'staff' || role === 'director') && !pl && <Btn label="+ Input P&L" onClick={() => { setForm({}); setShowForm(true); }} full />}
      {showForm && (
        <Sheet title={`${pl ? 'Edit' : 'Input'} P&L · ${toMonthLabel(monthKey)}`} onClose={() => setShowForm(false)}>
          {/* Ritase Pergi/Balikan/Waste Counter removed 2026-09-16 -- the
              server fills these in itself from the `ritase` table, the
              same source the Ritase tab already computes from. Nothing
              here needs a Finance Controller to retype them by hand.
              Laba Kotor % removed the same day -- it's just Laba Kotor /
              Pendapatan, both already below; the server computes it. */}
          {[['Pendapatan','pendapatan'],['Laba Kotor','labaKotor'],['Beban Operasional','bebanOps'],['Laba Bersih (Accounting)','labaBersihAccounting'],['Laba Bersih (Cash)','labaBersihCash'],['Selisih Outstanding','selisih']].map(([l,k]) => (
            <Inp key={k} placeholder={l} type="number" value={form[k]} onChange={v => setForm({...form,[k]:v})} />
          ))}
          <Btn label={saving?'Menyimpan...':'Simpan'} onClick={save} disabled={saving} full />
          <Btn label="Batal" onClick={() => setShowForm(false)} outline full />
        </Sheet>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// PLAN LOADER (Finance only)
// ══════════════════════════════════════════════════════════
function PlanLoader({ onRefresh, monthKey, data, role }) {
  const [files, setFiles] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [resetting, setResetting] = useState(false);

  const hasActivePlan = !!data?.plan;
  // Replacing a plan wipes the month's requests/amendments/ritase via
  // DELETE /api/plans/reset, which the server only allows for the director.
  const canReplace = role === 'director';

  useEffect(() => {
    setLoading(true);
    api.listSharepointFiles(monthKey).then(d => setFiles(d.files)).catch(() => setFiles([])).finally(() => setLoading(false));
  }, [monthKey]);

  const load = async f => {
    const action = hasActivePlan ? 'ganti' : 'load';
    const msg = hasActivePlan
      ? `Ganti plan bulan ini dengan "${f.name}"?\n\nSemua requests, amendments, dan ritase bulan ini akan dihapus. P&L tidak terpengaruh.`
      : `Load "${f.name}" sebagai plan bulan ini?`;
    if (!window.confirm(msg)) return;

    setLoadingId(f.id);
    try {
      // If plan exists, reset first
      if (hasActivePlan) {
        setResetting(true);
        await api.resetPlan(monthKey);
        setResetting(false);
      }
      await api.loadPlan({ itemId: f.id, fileName: f.name, uploadedBy: 'Finance Controller' });
      alert('Plan berhasil dimuat!');
      onRefresh();
    } catch (e) { alert(e.message); }
    setResetting(false);
    setLoadingId(null);
  };

  if (loading) return <Spin />;

  return (
    <div>
      {hasActivePlan && (
        <Card accent="#FAC775" style={{ background: '#FAEEDA', marginBottom: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 500, color: '#633806' }}>⚠ Plan aktif: {data.plan.month} · v{data.plan.version}</div>
          <div style={{ fontSize: 11, color: '#633806', marginTop: 4 }}>
            {canReplace
              ? 'Memuat file baru akan menghapus semua requests, amendments, dan ritase bulan ini. P&L tidak terpengaruh.'
              : 'Ganti plan hanya bisa dilakukan oleh Director.'}
          </div>
        </Card>
      )}

      <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>File Excel dari SharePoint · Cashflow/_Approved</div>

      {(!files || files.length === 0) && (
        <Empty msg={`Tidak ada file Excel untuk bulan ini di Cashflow/_Approved. Upload SKL_Cashflow_${monthKey?.replace('-','')}.xlsx ke SharePoint.`} />
      )}

      {files && files.map(f => (
        <Card key={f.id}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 500, color: C.black }}>{f.name}</div>
              <div style={{ fontSize: 11, color: '#888' }}>{new Date(f.lastModified).toLocaleDateString('id-ID')} · {(f.size/1024).toFixed(0)} KB</div>
            </div>
            {(!hasActivePlan || canReplace) && (
              <Btn
                label={resetting ? 'Menghapus...' : loadingId===f.id ? 'Loading...' : hasActivePlan ? 'Ganti Plan' : 'Load'}
                onClick={() => load(f)}
                disabled={!!loadingId || resetting}
                color={hasActivePlan ? '#633806' : C.crimson}
              />
            )}
          </div>
        </Card>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// REQUEST SHEET (staff submits from plan line)
// ══════════════════════════════════════════════════════════
// Tipe values that actually produce a ritase row -- see autoApprove() in
// server/routes/requests.js. Only these need the "Bulan rit" field at all.
// 'UJ Balikan' was missing here until 2026-09-17 -- CSK/CAN/Tedco requests
// with this tipe never showed the "Bulan rit" field even though they're
// ritase-eligible server-side (see RITASE_REQUIRES_TRUCK in requests.js,
// same set, kept in sync manually since this file has no shared import
// with the server).
const RITASE_TIPE = ['UJ Lampung', 'UJ Waste Lampung', 'UJ Balikan', 'Balikan'];

function RequestSheet({ line, monthKey, planId, userName, onClose, onDone }) {
  // Pre-filling from a plan line whose own tipe doesn't fit its customer's
  // pattern (a legacy mismatch -- e.g. an old CSK-customer plan_line
  // uploaded with tipe=Operasional, exactly the bug that caused missing
  // rits) would otherwise leave the Tipe select showing a value that
  // isn't even in its own (now customer-narrowed) option list. Falls back
  // to that customer's first valid tipe instead of trusting a possibly-
  // wrong pre-fill.
  const initialCustomer = CUSTOMER_ALIASES[line?.customer] || line?.customer || 'Lapak';
  const initialTipeOptions = tipeOptionsFor(initialCustomer);
  const initialTipe = line?.tipe && initialTipeOptions.includes(line.tipe) ? line.tipe : initialTipeOptions[0];

  const [form, setForm] = useState({
    keterangan: line?.keterangan || '',
    tipe: initialTipe,
    customer: initialCustomer,
    noTruk: line?.no_truk || '',
    amount: line?.pengeluaran || line?.penerimaan || '',
    requestedBy: userName || '',
    // Defaults to the current plan month -- most submissions ARE for a
    // trip that happened this month, so this only needs to be touched
    // when logging something late (see monthKeyMinus/ritase_month_key).
    ritaseMonthKey: monthKey,
  });
  const [saving, setSaving] = useState(false);

  // Which GPS trip this payment is for (2026-09-27). Lists the truck's
  // recent trips of the kind this tipe pays for (GET /api/ritase/trips) and
  // pre-selects the oldest one nobody has paid yet. Saved on the request;
  // skl-route-agent's nightly match uses it instead of guessing from dates.
  const [trips, setTrips] = useState(null);   // null = not looked up
  const [tripAt, setTripAt] = useState('');
  const truckDigits = form.noTruk.replace(/\D/g, '');
  const wantsTrip = RITASE_TIPE.includes(form.tipe) && truckDigits.length >= 4;
  useEffect(() => {
    if (!wantsTrip) { setTrips(null); setTripAt(''); return; }
    let live = true;
    const t = setTimeout(() => {
      api.tripsFor(truckDigits, form.tipe).then(d => {
        if (!live) return;
        const list = d.trips || [];
        setTrips(list);
        const unpaid = list.filter(x => !x.paid);
        setTripAt(unpaid.length ? unpaid[unpaid.length - 1].at : '');   // list is newest first
      }).catch(() => live && setTrips([]));
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [wantsTrip, truckDigits, form.tipe]);
  const pickedTrip = (trips || []).find(x => x.at === tripAt);

  // Customer drives which tipe values even show up (see
  // CUSTOMER_TIPE_OPTIONS) -- switching customer resets tipe to that
  // customer's first valid option rather than leaving a stale, likely-
  // invalid value selected.
  const setCustomer = c => setForm(f => ({ ...f, customer: c, tipe: tipeOptionsFor(c)[0] }));

  const submit = async (confirmDuplicate) => {
    if (!form.keterangan || !form.amount || !form.requestedBy) return alert('Keterangan, jumlah, dan nama wajib diisi');
    if (RITASE_TIPE.includes(form.tipe) && !form.noTruk.trim()) {
      return alert(`No. truk wajib diisi untuk tipe ${TIPE_LABELS[form.tipe] || form.tipe} -- tanpa ini trip tidak akan terhitung di Ritase`);
    }
    // The amount is pre-filled from the plan, and submitting it unchanged
    // is how actual payments drifted from the plan unnoticed (UJ 9507 kept
    // its 5.6M plan amount after its 50rb deduction ended -- Sept 2026
    // reconciliation). Ask once whether the plan figure is really what was
    // transferred. Skipped on the duplicate-resubmit path (already asked).
    const planAmount = line ? (line.pengeluaran || line.penerimaan || 0) : 0;
    const amountNow = parseInt(String(form.amount).replace(/\D/g, '')) || 0;
    if (confirmDuplicate === undefined && planAmount > 0 && amountNow === planAmount) {
      if (!window.confirm(`Jumlah sama dengan plan: Rp ${fmtFull(planAmount)}.\n\nApakah ini jumlah yang benar-benar ditransfer?\n\nOK = ya, kirim. Batal = ubah jumlahnya dulu.`)) return;
    }
    setSaving(true);
    try {
      const result = await api.createRequest({
        monthKey, planId,
        planLineId: line?.id || null,
        keterangan: form.keterangan,
        tipe: form.tipe,
        customer: form.customer,
        noTruk: form.noTruk,
        amount: parseInt(String(form.amount).replace(/\D/g, '')),
        requestedBy: form.requestedBy,
        requestDate: today(),
        ritaseMonthKey: form.ritaseMonthKey !== monthKey ? form.ritaseMonthKey : undefined,
        tripAt: pickedTrip ? pickedTrip.at : undefined,
        tripLabel: pickedTrip ? pickedTrip.label : undefined,
        confirmDuplicate,
      });
      onDone(result);
    } catch (e) {
      // Server flags this specifically (see DUPLICATE_WINDOW_MIN in
      // server/routes/requests.js) when the same plan line already got a
      // request in the last few minutes -- confirmed live 2026-09-03,
      // truck 9493's advance submitted twice 16s apart, both approved by
      // mistake before anyone noticed they were duplicates. Ask once
      // instead of silently blocking (a genuinely new trip on the same
      // line minutes later is rare but real) or silently allowing it.
      if (e.body?.duplicate) {
        if (window.confirm(`${e.message}\n\nKirim ulang?`)) return submit(true);
      } else {
        alert(e.message);
      }
    }
    setSaving(false);
  };

  // Format amount for display — show with thousand separators
  const displayAmount = form.amount
    ? Number(String(form.amount).replace(/\D/g, '')).toLocaleString('id-ID')
    : '';

  const currency = 'Rp'; // future: pass as prop per entity

  return (
    <Sheet title={line ? 'Request Disbursement' : 'Request Baru'} onClose={onClose}>
      {line && (
        <div style={{ background: '#E1F5EE', border: '0.5px solid #5DCAA5', borderRadius: 8, padding: '8px 12px', marginBottom: 12 }}>
          <div style={{ fontSize: 11, color: '#085041', fontWeight: 500 }}>✓ Dari plan line — sudah pre-filled</div>
        </div>
      )}
      <Inp placeholder="Keterangan" value={form.keterangan} onChange={v => setForm({...form, keterangan: v})} />
      {/* Customer picked first -- it drives which Tipe options even show up
          (see CUSTOMER_TIPE_OPTIONS/tipeOptionsFor), so a wrong pairing
          like Operasional-for-a-CSK-advance is structurally not choosable
          instead of merely discouraged. */}
      <Sel value={form.customer} onChange={setCustomer} options={CUSTOMER_SELECT_OPTIONS} />
      <Sel
        value={form.tipe}
        onChange={v => setForm({...form, tipe: v})}
        options={tipeOptionsFor(form.customer).map(t => ({ value: t, label: TIPE_LABELS[t] || t }))}
      />
      <Inp placeholder={RITASE_TIPE.includes(form.tipe) ? 'No Truk (wajib untuk tipe ini)' : 'No Truk (jika ada)'} value={form.noTruk} onChange={v => setForm({...form, noTruk: v})} />
      {wantsTrip && trips !== null && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>Trip yang dibayar (dari GPS)</div>
          <select
            value={tripAt}
            onChange={e => setTripAt(e.target.value)}
            style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 13, background: C.white, color: C.black, boxSizing: 'border-box' }}
          >
            {trips.map(x => (
              <option key={x.at} value={x.at}>{x.label} · {x.paid ? 'sudah dibayar' : 'belum dibayar'}</option>
            ))}
            <option value="">Belum berangkat / tidak ada di daftar</option>
          </select>
          {trips.length === 0 && (
            <div style={{ fontSize: 11, color: '#888', marginTop: 4 }}>Belum ada trip GPS truk ini dalam 14 hari terakhir. Tetap bisa disimpan.</div>
          )}
          {pickedTrip?.paid && (
            <div style={{ fontSize: 11, color: '#633806', marginTop: 4 }}>⚠ Trip ini sudah ada pembayarannya. Pastikan bukan pembayaran dobel.</div>
          )}
        </div>
      )}
      {RITASE_TIPE.includes(form.tipe) && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>
            Bulan rit (kapan trip-nya beneran terjadi — biasanya sama, ubah hanya kalau ini rit bulan lalu yang baru dilaporkan)
          </div>
          <select
            value={form.ritaseMonthKey}
            onChange={e => setForm({...form, ritaseMonthKey: e.target.value})}
            style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '9px 12px', fontSize: 13, background: C.white, color: C.black, boxSizing: 'border-box' }}
          >
            {[0, 1, 2].map(n => {
              const k = monthKeyMinus(monthKey, n);
              return <option key={k} value={k}>{toMonthLabel(k)}{n === 0 ? ' (bulan ini)' : ''}</option>;
            })}
          </select>
        </div>
      )}
      {/* Amount field — formatted display, raw storage */}
      <div style={{ position: 'relative', marginBottom: 10 }}>
        <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#888', pointerEvents: 'none' }}>{currency}</div>
        <input
          type="text"
          inputMode="numeric"
          placeholder="0"
          value={displayAmount}
          onChange={e => {
            const raw = e.target.value.replace(/\D/g, '');
            setForm({...form, amount: raw});
          }}
          style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '9px 12px 9px 36px', fontSize: 13, background: C.white, color: C.black, boxSizing: 'border-box' }}
        />
      </div>
      <Inp placeholder="Nama pengaju (wajib)" value={form.requestedBy} onChange={v => setForm({...form, requestedBy: v})} />
      {/* onClick={() => submit()}, NOT onClick={submit} -- submit now takes
          a confirmDuplicate param, and Btn forwards the raw click event
          straight through as the handler's first arg (see Btn above), so
          onClick={submit} would pass the SyntheticEvent as confirmDuplicate
          on the very first press -- truthy, silently skipping the
          duplicate-submit check below on every single submit. */}
      <Btn label={saving?'Mengirim...':'Kirim Request'} onClick={() => submit()} disabled={saving} full />
      <Btn label="Batal" onClick={onClose} outline full />
    </Sheet>
  );
}

// ══════════════════════════════════════════════════════════
// AMEND SHEET (staff amends a plan line or adds new)
// ══════════════════════════════════════════════════════════
function AmendSheet({ line, amendType: initType, monthKey, planId, userName, onClose, onDone }) {
  const [type] = useState(initType || (line ? 'amount_change' : 'add_disbursement'));
  // Editing an existing line supports selecting more than one field to change
  // at once (e.g. amount + date together) — each still becomes its own
  // amendment under the hood (they stack on the line), submitted in one go
  // with a shared reason, so staff don't have to repeat the round trip.
  // Was `new Set(line ? ['amount_change'] : [])` -- pre-selecting Amount
  // Change for any existing line meant hasEdits was true the instant the
  // sheet opened, which disables Cancel Line via the mutual-exclusivity
  // guard below (line ~1383/1468). Staff had no visible way to know
  // clicking the already-active Amount Change button would deselect it
  // and unlock Cancel Line -- confirmed real feedback: "cancel line always
  // greyed out". Nothing depends on a type being pre-selected (submit
  // already alerts "Pilih minimal satu jenis perubahan" on an empty
  // selection), so starting empty for both new and existing lines removes
  // the bug at its source instead of hacking around the disabled check.
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [form, setForm] = useState({ requestedBy: userName || '' });
  const [saving, setSaving] = useState(false);

  const isNew = type === 'add_disbursement' || type === 'add_inflow';
  const hasCancel = selectedTypes.has('cancel_line');
  const hasEdits = [...selectedTypes].some(t => t !== 'cancel_line');

  const toggleType = t => {
    setSelectedTypes(prev => {
      if (t === 'cancel_line') return prev.has(t) ? new Set() : new Set(['cancel_line']);
      const next = new Set(prev);
      next.delete('cancel_line');
      next.has(t) ? next.delete(t) : next.add(t);
      return next;
    });
  };

  // Build [originalValue, requestedValue] for one field-change type — throws
  // (with a user-facing message) if that type's input hasn't been filled in.
  const buildFieldChange = t => {
    if (t === 'amount_change') {
      if (!form.newAmount) throw new Error('Jumlah baru wajib diisi');
      return [`Rp ${fmtFull(line.pengeluaran || line.penerimaan)}`, `Rp ${fmtFull(parseInt(form.newAmount))}`];
    }
    if (t === 'date_change') {
      if (!form.newDate) throw new Error('Tanggal baru wajib diisi');
      // original_value must stay a real ISO date (matching requested_value's
      // format) - the dashboard's "confirmed" baseline reverts to this value
      // and matches it against day buckets, which needs a real date, not the
      // human-readable "27 Agu" this used to send (bug found + fixed
      // 2026-08-27 - silently dropped amended lines from the whole month's
      // confirmed total instead of reverting them). Display code formats
      // both sides with dateLabel() at render time instead.
      return [line.tanggal, form.newDate];
    }
    if (t === 'description_change') {
      if (!form.newDescription) throw new Error('Keterangan baru wajib diisi');
      return [line.keterangan, form.newDescription];
    }
    if (t === 'customer_change') {
      if (!form.newCustomer) throw new Error('Customer baru wajib dipilih');
      return [line.customer || '—', form.newCustomer];
    }
    if (t === 'tipe_change') {
      if (!form.newTipe) throw new Error('Tipe baru wajib dipilih');
      return [line.tipe || '—', form.newTipe];
    }
    // cancel_line
    const amount = line.pengeluaran > 0 ? line.pengeluaran : line.penerimaan;
    const direction = line.pengeluaran > 0 ? 'Pengeluaran' : 'Penerimaan';
    return [`${line.keterangan} · ${direction} Rp ${fmtFull(amount)}`, 'CANCEL'];
  };

  const submit = async () => {
    if (!form.reason) return alert('Alasan wajib diisi');
    setSaving(true);
    try {
      if (isNew) {
        if (!form.keterangan || !form.amount) { alert('Keterangan dan jumlah wajib diisi'); setSaving(false); return; }
        // customer/tipe/date used to silently default (to 'Other'/
        // 'Operasional'-or-'Penerimaan'/today()) instead of being required --
        // the exact bug class that let a real plan line get created with a
        // wrong tipe (see the 2026-09-03 incident noted above) or a date that
        // silently isn't the one actually intended, quietly shifting the
        // month's cashflow/deficit-day projection (dashboard.js buckets every
        // plan_line strictly by its tanggal). Now required outright, plus
        // no_truk when the tipe needs one, same rule as RequestSheet.
        if (!form.customer) { alert('Customer wajib dipilih'); setSaving(false); return; }
        if (!form.tipe) { alert('Tipe wajib dipilih'); setSaving(false); return; }
        if (!addTipeOptionsFor(form.customer, type).includes(form.tipe)) {
          alert(type === 'add_disbursement' ? `Tipe ${form.tipe} adalah uang masuk -- pakai Tambah Penerimaan` : `Tipe ${form.tipe} adalah uang keluar -- pakai Tambah Pengeluaran`);
          setSaving(false); return;
        }
        if (!form.date) { alert('Tanggal wajib diisi'); setSaving(false); return; }
        if (RITASE_TIPE.includes(form.tipe) && !(form.noTruk || '').trim()) {
          alert(`No. truk wajib diisi untuk tipe ${TIPE_LABELS[form.tipe] || form.tipe} -- tanpa ini trip tidak akan terhitung di Ritase`);
          setSaving(false); return;
        }
        const requestedValue = JSON.stringify({
          keterangan: form.keterangan,
          tipe: form.tipe,
          customer: form.customer,
          no_truk: form.noTruk || '',
          tanggal: form.date,
          pengeluaran: type === 'add_disbursement' ? parseInt(String(form.amount).replace(/\D/g,'')) : 0,
          penerimaan: type === 'add_inflow' ? parseInt(String(form.amount).replace(/\D/g,'')) : 0,
        });
        const result = await api.createAmendment({
          planId, planLineId: null, monthKey,
          amendmentType: type, originalValue: null, requestedValue,
          reason: form.reason, requestedBy: form.requestedBy || userName || 'Staff',
        });
        onDone(result);
      } else {
        if (selectedTypes.size === 0) { alert('Pilih minimal satu jenis perubahan'); setSaving(false); return; }
        // Validate + build every selected change before sending any of them.
        const entries = [...selectedTypes].map(t => [t, ...buildFieldChange(t)]);
        let result = null;
        for (const [amendmentType, originalValue, requestedValue] of entries) {
          result = await api.createAmendment({
            planId, planLineId: line.id, monthKey,
            amendmentType, originalValue, requestedValue,
            reason: form.reason, requestedBy: form.requestedBy || userName || 'Staff',
          });
        }
        onDone(result);
      }
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <Sheet title={line ? `Amend: ${line.keterangan}` : type === 'add_disbursement' ? 'Tambah Pengeluaran Baru' : 'Tambah Penerimaan Baru'} onClose={onClose}>
      {/* Type selector — multi-select for existing lines (combine changes) */}
      {line && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginBottom: 6 }}>
            {['amount_change','date_change','description_change','customer_change','tipe_change','cancel_line'].map(t => {
              const st = AMEND_TYPE[t];
              const active = selectedTypes.has(t);
              const disabled = t === 'cancel_line' ? hasEdits : hasCancel;
              return (
                <button key={t} disabled={disabled} onClick={() => toggleType(t)} style={{
                  padding: '8px 6px', fontSize: 11, fontWeight: active ? 500 : 400,
                  border: active ? `1.5px solid ${st.border}` : `0.5px solid ${C.border}`, borderRadius: 8,
                  background: active ? st.bg : C.white, color: disabled ? '#CCC' : active ? st.color : '#888',
                  cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
                }}>
                  {st.label}
                </button>
              );
            })}
          </div>
          <div style={{ fontSize: 10, color: '#B4B2A9', marginBottom: 14 }}>Bisa pilih lebih dari satu, misal Ubah Jumlah + Ubah Tanggal sekaligus</div>
        </>
      )}

      {/* Fields by type */}
      {selectedTypes.has('amount_change') && (
        <>
          <div style={{ background: '#F1EFE8', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#888' }}>Jumlah saat ini</div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>Rp {fmtFull(line?.pengeluaran || line?.penerimaan || 0)}</div>
          </div>
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#888', pointerEvents: 'none' }}>Rp</div>
            <input type="text" inputMode="numeric" placeholder="0"
              value={form.newAmount ? Number(String(form.newAmount).replace(/\D/g,'')).toLocaleString('id-ID') : ''}
              onChange={e => setForm({...form, newAmount: e.target.value.replace(/\D/g,'')})}
              style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '9px 12px 9px 36px', fontSize: 13, background: C.white, color: C.black, boxSizing: 'border-box' }} />
          </div>
        </>
      )}
      {selectedTypes.has('date_change') && (
        <>
          <div style={{ background: '#F1EFE8', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#888' }}>Tanggal saat ini</div>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{dateLabel(line?.tanggal)}</div>
          </div>
          <Inp placeholder="Tanggal baru" type="date" value={form.newDate} onChange={v => setForm({...form, newDate: v})} />
        </>
      )}
      {selectedTypes.has('cancel_line') && (
        <div style={{ background: '#FCEBEB', border: '0.5px solid #F09595', borderRadius: 8, padding: '10px 12px', marginBottom: 10 }}>
          <div style={{ fontSize: 13, color: '#A32D2D' }}>Line ini akan di-cancel dari plan</div>
        </div>
      )}
      {selectedTypes.has('description_change') && (
        <>
          <div style={{ background: '#F1EFE8', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#888' }}>Keterangan saat ini</div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{line?.keterangan}</div>
          </div>
          <Inp placeholder="Keterangan baru" value={form.newDescription} onChange={v => setForm({...form, newDescription: v})} />
        </>
      )}
      {selectedTypes.has('customer_change') && (
        <>
          <div style={{ background: '#F1EFE8', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#888' }}>Customer saat ini</div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{line?.customer || '—'}</div>
          </div>
          <Sel value={form.newCustomer} onChange={v => setForm({...form, newCustomer: v})} options={CUSTOMER_SELECT_OPTIONS} placeholder="Pilih Customer baru" />
          <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>Tipe dan No Truk tidak bisa diubah</div>
        </>
      )}
      {selectedTypes.has('tipe_change') && (
        <>
          <div style={{ background: '#F1EFE8', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: '#888' }}>Tipe saat ini</div>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{line?.tipe || '—'}</div>
          </div>
          <Sel value={form.newTipe} onChange={v => setForm({...form, newTipe: v})} options={TIPE} placeholder="Pilih Tipe baru" />
          <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
            Untuk reklasifikasi line lama (mis. jadi Pinjaman) — arah pengeluaran/penerimaan line tidak berubah, hanya labelnya
          </div>
        </>
      )}
      {isNew && (
        <>
          <Inp placeholder="Keterangan" value={form.keterangan} onChange={v => setForm({...form, keterangan: v})} />
          {/* Customer picked first, same cascading pattern as RequestSheet
              (CUSTOMER_TIPE_OPTIONS/tipeOptionsFor) -- this form used to
              offer a hardcoded, stale flat tipe list here (still had
              'Balikan' with no 'UJ Balikan' at all), which let a plan line
              get created with a tipe the request side no longer recognizes
              for that customer. Confirmed live 2026-09-03: a plan line
              created here for CAN with tipe='Balikan' caused every real
              request against it (submitted with the correct 'UJ Balikan')
              to be misclassified as an Exception needing Director
              approval, since classifyTier() matches tipe as an exact
              string. Cascading here the same way closes that gap
              structurally instead of relying on staff picking the right
              one from an unfiltered list. */}
          <Sel value={form.customer} onChange={v => setForm({...form, customer: v, tipe: addTipeOptionsFor(v, type)[0]})} options={CUSTOMER_SELECT_OPTIONS} placeholder="Pilih Customer" />
          <Sel
            value={form.tipe}
            onChange={v => setForm({...form, tipe: v})}
            options={addTipeOptionsFor(form.customer, type).map(t => ({ value: t, label: TIPE_LABELS[t] || t }))}
            placeholder="Pilih Tipe"
          />
          {form.customer && addTipeOptionsFor(form.customer, type).length === 0 && (
            <div style={{ fontSize: 11, color: '#A32D2D', marginBottom: 10 }}>
              {type === 'add_disbursement'
                ? 'Customer ini hanya punya tipe uang masuk -- pakai Tambah Penerimaan'
                : 'Customer ini hanya punya tipe uang keluar -- pakai Tambah Pengeluaran'}
            </div>
          )}
          {RITASE_TIPE.includes(form.tipe) && <Inp placeholder="No Truk (wajib untuk tipe ini)" value={form.noTruk} onChange={v => setForm({...form, noTruk: v})} />}
          <div style={{ position: 'relative', marginBottom: 10 }}>
            <div style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#888', pointerEvents: 'none' }}>Rp</div>
            <input type="text" inputMode="numeric" placeholder="0"
              value={form.amount ? Number(String(form.amount).replace(/\D/g,'')).toLocaleString('id-ID') : ''}
              onChange={e => setForm({...form, amount: e.target.value.replace(/\D/g,'')})}
              style={{ width: '100%', border: `0.5px solid ${C.border}`, borderRadius: 8, padding: '9px 12px 9px 36px', fontSize: 13, background: C.white, color: C.black, boxSizing: 'border-box' }} />
          </div>
          <Inp placeholder="Tanggal" type="date" value={form.date} onChange={v => setForm({...form, date: v})} />
          <DirectionBanner inflow={type === 'add_inflow'} amount={parseInt(String(form.amount || '').replace(/\D/g, '')) || 0} />
        </>
      )}

      <Inp placeholder="Nama pengaju" value={form.requestedBy} onChange={v => setForm({...form, requestedBy: v})} />
      <Inp placeholder="Alasan (wajib)" value={form.reason} onChange={v => setForm({...form, reason: v})} />
      <Btn label={saving?'Mengirim...':'Kirim Amendment'} onClick={submit} disabled={saving} full color="#3C3489" />
      <Btn label="Batal" onClick={onClose} outline full />
    </Sheet>
  );
}

// ══════════════════════════════════════════════════════════
// REVERSE SHEET — Director reverses an approved disbursement
// ══════════════════════════════════════════════════════════
function ReverseSheet({ line, monthKey, userName, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reason) return alert('Alasan wajib diisi');
    setSaving(true);
    try {
      await api.reversePayment({
        planLineId: line.id,
        monthKey,
        reason,
        reversedBy: userName,
      });
      onDone();
    } catch (e) { alert(e.message); }
    setSaving(false);
  };

  return (
    <Sheet title="Batal Pembayaran" onClose={onClose}>
      <div style={{ background: '#FCEBEB', border: '0.5px solid #F09595', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 500, color: '#A32D2D', marginBottom: 4 }}>↩ Membatalkan pembayaran yang sudah diapprove</div>
        <div style={{ fontSize: 11, color: '#A32D2D' }}>{line.keterangan}</div>
        <div style={{ fontSize: 11, color: '#A32D2D', marginTop: 2 }}>
          {line.tipe} · {line.pengeluaran > 0 ? `-Rp ${fmt(line.pengeluaran)}` : `+Rp ${fmt(line.penerimaan)}`}
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
        Ini akan:
        <br />• Set plan line kembali ke Upcoming
        <br />• Cancel request yang terkait
        {['UJ Lampung','UJ Waste Lampung','Balikan'].includes(line.tipe) && <><br />• Kurangi ritase counter</>}
      </div>
      <Inp placeholder="Alasan pembatalan (wajib)" value={reason} onChange={v => setReason(v)} />
      <Inp placeholder="Nama" value={userName} onChange={() => {}} style={{ background: '#F8F7F4', color: '#888' }} />
      <Btn label={saving ? 'Memproses...' : '↩ Ya, Batalkan Pembayaran'} onClick={submit} disabled={saving} full color="#A32D2D" />
      <Btn label="Batal" onClick={onClose} outline full />
    </Sheet>
  );
}

// ══════════════════════════════════════════════════════════
// LOGIN SCREEN
// ══════════════════════════════════════════════════════════
function LoginScreen({ error }) {
  return (
    <div style={{ minHeight: '100vh', background: C.crimson, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ fontFamily: 'Georgia, serif', fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 8, letterSpacing: '0.05em' }}>✱ Sumber Kasih Lestari</div>
      {/* Only "Sumber Kasih Lestari" on the login, no Mercusuar (Director, 3 Oct). */}
      <div style={{ fontFamily: 'Georgia, serif', fontSize: 32, fontWeight: 500, color: C.white, marginBottom: 48 }}>Arus Kas</div>

      {error && (
        <div style={{ background: 'rgba(255,255,255,0.1)', border: '0.5px solid rgba(255,255,255,0.3)', borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 12, color: '#FFD0CC', maxWidth: 320, textAlign: 'center' }}>
          {error}
        </div>
      )}

      <a href="/auth/login" style={{
        display: 'block', background: C.white, color: C.crimson,
        borderRadius: 12, padding: '14px 32px', fontSize: 14, fontWeight: 600,
        textDecoration: 'none', textAlign: 'center', marginBottom: 16,
        boxShadow: '0 2px 12px rgba(0,0,0,0.2)',
      }}>
        🔐 Login dengan Microsoft
      </a>

      <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', textAlign: 'center', maxWidth: 280 }}>
        Gunakan akun Microsoft kantor kamu
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ROOT APP
// ══════════════════════════════════════════════════════════
export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [monthKey, setMonthKey] = useState(CURRENT_MONTH_KEY);
  const [tab, setTab] = useState(tabFromHash);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestSheet, setRequestSheet] = useState(null);
  const [amendSheet, setAmendSheet] = useState(null);
  const [reverseSheet, setReverseSheet] = useState(null);
  const [appVersion, setAppVersion] = useState('');
  // Four-tab redesign (2026-10-01) is the default; "Tampilan lama" in
  // Lainnya switches back to these screens for anyone who needs them
  // during the rollout. Remembered per browser.
  const [ui, setUiState] = useState(() => { try { return localStorage.getItem('skl-ui') || 'new'; } catch { return 'new'; } });
  const setUi = v => { try { localStorage.setItem('skl-ui', v); } catch {} if (v === 'old') window.history.replaceState(null, '', '#overview'); setUiState(v); if (v === 'old') setTab('overview'); };

  // Fetch app version once on mount
  useEffect(() => {
    api.health().then(h => setAppVersion(h.version || '')).catch(() => {});
  }, []);

  // Keep the URL hash in sync with the active tab (replaceState so every
  // tab click doesn't spam browser history) and pick up back/forward or a
  // manually-edited hash. This is what makes a page refresh land back on
  // the tab you were on instead of always resetting to Overview.
  useEffect(() => {
    // Only in the old UI: the redesign keeps its own tab in the hash.
    if (ui === 'old' && window.location.hash.slice(1) !== tab) window.history.replaceState(null, '', `#${tab}`);
  }, [tab, ui]);
  useEffect(() => {
    const onHashChange = () => setTab(tabFromHash());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const monthLabel = toMonthLabel(monthKey);
  const prevMonth = () => {
    const [y, m] = monthKey.split('-').map(Number);
    const d = new Date(y, m - 2, 1);
    setMonthKey(toMonthKey(d));
    setTab('overview');
  };
  const nextMonth = () => {
    const [y, m] = monthKey.split('-').map(Number);
    const d = new Date(y, m, 1);
    setMonthKey(toMonthKey(d));
    setTab('overview');
  };

  // ── Handle auth on mount ─────────────────────────────────
  useEffect(() => {
    // The role lives in app_users (Pengguna & akses, in the SKL Hub) and can
    // change without a new login; the account menu also needs the role in
    // each app. Take both from the server, after a fresh login or a reload.
    const syncMe = () => api.me().then(r => {
      if (!r?.user?.role) return;
      const cur = JSON.parse(sessionStorage.getItem('skl_user') || '{}');
      const u = { ...cur, role: r.user.role, apps: r.apps || null };
      sessionStorage.setItem('skl_user', JSON.stringify(u));
      setUser(u);
    }).catch(() => {});
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const name = params.get('name');
    const role = params.get('role');
    const email = params.get('email');
    const authErr = params.get('auth_error');

    if (authErr) {
      setAuthError(decodeURIComponent(authErr));
      setAuthLoading(false);
      window.history.replaceState({}, '', '/');
      return;
    }

    if (token) {
      // Fresh login — store and clean URL
      sessionStorage.setItem('skl_token', token);
      sessionStorage.setItem('skl_user', JSON.stringify({ name: decodeURIComponent(name), role, email: decodeURIComponent(email) }));
      window.history.replaceState({}, '', '/');
      setUser({ name: decodeURIComponent(name), role, email: decodeURIComponent(email) });
      setAuthLoading(false);
      syncMe(); // a fresh login needs the per-app roles too (15 Oct: no "Ops App" in the menu right after signing in)
      return;
    }

    // Check existing session
    const stored = sessionStorage.getItem('skl_user');
    const storedToken = sessionStorage.getItem('skl_token');
    if (stored && storedToken && !isTokenExpired(storedToken)) {
      setUser(JSON.parse(stored));
      setAuthLoading(false);
      syncMe();
      return;
    }

    // No session, or a stale/expired one (client-side exp pre-check --
    // UX hint only, see isTokenExpired) -- and this isn't a bounce-back
    // from a failed login attempt (that's the authErr branch above). Skip
    // the static "Login dengan Microsoft" screen entirely (no flash) and
    // go straight to Microsoft, instead of a doomed API-call/401/redirect
    // round trip.
    sessionStorage.removeItem('skl_token');
    sessionStorage.removeItem('skl_user');
    window.location.href = '/auth/login';
  }, []);

  const logout = async () => {
    try {
      const { logoutUrl } = await api.logout();
      sessionStorage.removeItem('skl_token');
      sessionStorage.removeItem('skl_user');
      window.location.href = logoutUrl;
    } catch {
      sessionStorage.clear();
      window.location.reload();
    }
  };

  // Only the response for the month currently selected may land: clicking
  // ‹ › quickly fired several requests and whichever answered last won, so
  // the screen could show another month's data (found testing the redesign,
  // 2026-10-01).
  const wantMonth = useRef(monthKey);
  wantMonth.current = monthKey;
  // Clicking ‹ › quickly used to queue one full month load per click, so the
  // page sat on "Memuat…" until all of them came back (Director, 15 Oct:
  // "cancel old loads"). A new month aborts the previous month's request,
  // and the load waits 250 ms so a run of clicks only loads where it stops.
  const loadCtl = useRef(null);
  // Keyed on WHO is signed in (email), not the user object: refreshing the
  // role after a reload (/auth/me) replaced the object, started a second
  // load and cancelled the first (16 Oct: "Belum ada plan" for ~5 s after a
  // page refresh).
  const signedInAs = user?.email || null;
  const refresh = useCallback(async () => {
    if (!signedInAs) return;
    loadCtl.current?.abort();
    const ctl = new AbortController(); loadCtl.current = ctl;
    setLoading(true);
    try { const d = await api.dashboard(monthKey, ctl.signal); if (wantMonth.current === monthKey) setData(d); }
    catch (e) { if (e.name !== 'AbortError') console.error(e); }
    // only the latest load may end "loading" (a cancelled one finishing must not)
    if (loadCtl.current === ctl && wantMonth.current === monthKey) setLoading(false);
  }, [signedInAs, monthKey]);

  const firstLoad = useRef(true);
  useEffect(() => {
    if (!signedInAs) return;
    if (firstLoad.current) { firstLoad.current = false; refresh(); return; }
    const t = setTimeout(refresh, 250);
    return () => clearTimeout(t);
  }, [signedInAs, monthKey, refresh]);

  // ── Auth states ──────────────────────────────────────────
  if (authLoading) {
    return (
      <div style={{ minHeight: '100vh', background: C.crimson, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
        <div className="skl-spinner" style={{ border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff' }} />
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>Memuat...</div>
      </div>
    );
  }

  if (!user) return <LoginScreen error={authError} />;

  const role = user.role;

  if (ui !== 'old') {
    return <AppV2 api={api} user={user} monthKey={monthKey} setMonthKey={setMonthKey} data={data} loading={loading} refresh={refresh} logout={logout} appVersion={appVersion} setUi={setUi}
      legacy={{ RITASE_TIPE, monthKeyMinus, toMonthLabel, CUSTOMER_SELECT_OPTIONS, addTipeOptionsFor, TIPE_LABELS, PLScreen, PlanLoader, CloseMonthButton, Ritase, Reopen }} />;
  }

  const TABS = [
    { id: 'overview',   label: 'Overview' },
    { id: 'planlines',  label: 'Plan Lines' },
    { id: 'requests',   label: 'Requests', badge: (data?.requests?.pending || 0) + (data?.requests?.flagged || 0) },
    { id: 'amendments', label: 'Amendments', badge: data?.amendments?.pending },
    { id: 'reopen',     label: 'Reopen', badge: data?.reopen?.pending },
    { id: 'ritase',     label: 'Ritase' },
    { id: 'pl',         label: 'P&L' },
    { id: 'loadplan',   label: 'Load Plan' },
  ];

  const handleRequest = line => setRequestSheet(line);
  const handleAmend = (line, type) => setAmendSheet({ line, type: type || null });
  const handleAddNew = type => setAmendSheet({ line: null, type });
  const handleReverse = line => setReverseSheet(line);
  const handleReverseDone = () => { setReverseSheet(null); refresh(); alert('Pembayaran berhasil dibatalkan'); };
  const handleRequestDone = (result) => { setRequestSheet(null); refresh(); alert(result?.autoApproved ? 'Request auto-approved ✓' : (result?.message || 'Request dikirim — menunggu approval Director')); };
  const handleAmendDone = (result) => {
    setAmendSheet(null);
    refresh();
    if (result?.deficitCheck?.hasDeficit) {
      alert(`⚠ Amendment dikirim — PERINGATAN DEFISIT\n\nSaldo akan minus Rp ${fmt(Math.abs(result.deficitCheck.deficitAmount))} pada ${dateLabel(result.deficitCheck.deficitDate)} jika diapprove.\n\nDirector akan melihat peringatan ini sebelum approve.`);
    } else {
      alert('Amendment berhasil dikirim — menunggu approval Director');
    }
  };

  const roleLabel = { director: 'Director', staff: 'Staff' }[role] || role;
  const roleColor = { director: '#FAC775', staff: '#B5D4F4' }[role] || '#fff';

  return (
    <div className="skl-app">
      {/* Header */}
      <div className="skl-header">
        <div className="skl-header-inner">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div>
              {/* Was a link to the Hub - now redundant since "Hub ↗" is its own
                  explicit link below (added 2026-08-27), so this is back to
                  being plain brand text. */}
              <div className="skl-brand">✱ PT Sumber Kasih Lestari</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button onClick={prevMonth} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: C.white, fontSize: 16, cursor: 'pointer', borderRadius: 6, padding: '2px 8px', lineHeight: 1 }}>‹</button>
                <div className="skl-title">{monthLabel}</div>
                <button onClick={nextMonth} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: C.white, fontSize: 16, cursor: 'pointer', borderRadius: 6, padding: '2px 8px', lineHeight: 1 }}>›</button>
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 11, color: roleColor, fontWeight: 500 }}>{roleLabel}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>{user.name}</div>
              {/* Cross-link to the other SKL app — same Azure AD tenant, so this
                  is normally a silent re-auth rather than a real login prompt. */}
              <a href="https://skl.ptmercusuar.com" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', textDecoration: 'underline' }}>
                Hub ↗
              </a>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', margin: '0 5px' }}>·</span>
              <a href="https://skl-ops.ptmercusuar.com" style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', textDecoration: 'underline' }}>
                Ops ↗
              </a>
              <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', margin: '0 5px' }}>·</span>
              <button onClick={logout} style={{ fontSize: 10, color: 'rgba(255,255,255,0.5)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
                Logout
              </button>
              <div><button onClick={() => { window.history.replaceState(null, '', '#ringkasan'); setUi('new'); }} style={{ fontSize: 10, color: '#FAC775', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline', marginTop: 4 }}>
                Tampilan baru ›
              </button></div>
            </div>
          </div>
          <div className="skl-tabs">
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)} style={{ flexShrink: 0, padding: '9px 12px', fontSize: 12, border: 'none', cursor: 'pointer', background: 'transparent', color: tab===t.id?C.white:'rgba(255,255,255,0.45)', fontWeight: tab===t.id?500:400, borderBottom: tab===t.id?`2px solid ${C.white}`:'2px solid transparent', position: 'relative' }}>
                {t.label}
                {t.badge > 0 && <span style={{ position: 'absolute', top: 4, right: 2, background: '#FAC775', color: '#633806', fontSize: 9, fontWeight: 600, padding: '1px 4px', borderRadius: 8 }}>{t.badge}</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="skl-inner">
        <div className="skl-body">
          {tab === 'overview'   && <Dashboard data={data} loading={loading} role={role} onRequest={handleRequest} onAmend={line => handleAmend(line)} onReverse={handleReverse} onRefresh={refresh} onGoToRequests={() => setTab('requests')} />}
          {tab === 'planlines'  && <PlanLines data={data} role={role} onRequest={handleRequest} onAmend={line => handleAmend(line)} onAddNew={handleAddNew} onReverse={handleReverse} />}
          {tab === 'requests'   && <Requests data={data} role={role} onRefresh={refresh} />}
          {tab === 'amendments' && <Amendments data={data} role={role} onRefresh={refresh} />}
          {tab === 'reopen'     && <Reopen role={role} onRefresh={refresh} />}
          {tab === 'ritase'     && <Ritase data={data} />}
          {tab === 'pl'         && <PLScreen data={data} role={role} monthKey={monthKey} onRefresh={refresh} />}
          {tab === 'loadplan'   && <PlanLoader onRefresh={refresh} monthKey={monthKey} data={data} role={role} />}
        </div>
      </div>

      {/* Sheets */}
      {requestSheet !== undefined && requestSheet !== null && (
        <RequestSheet
          line={requestSheet === true ? null : requestSheet}
          monthKey={monthKey}
          planId={data?.plan?.id}
          userName={user.name}
          onClose={() => setRequestSheet(null)}
          onDone={handleRequestDone}
        />
      )}
      {amendSheet && (
        <AmendSheet
          line={amendSheet.line}
          amendType={amendSheet.type}
          monthKey={monthKey}
          planId={data?.plan?.id}
          userName={user.name}
          onClose={() => setAmendSheet(null)}
          onDone={handleAmendDone}
        />
      )}

      {reverseSheet && (
        <ReverseSheet
          line={reverseSheet}
          monthKey={monthKey}
          userName={user.name}
          onClose={() => setReverseSheet(null)}
          onDone={handleReverseDone}
        />
      )}

      {/* Version */}
      <div style={{ textAlign: 'center', padding: '16px 0 28px', fontSize: 10, color: '#C4B8B8' }}>
        {appVersion ? `Arus Kas · v${appVersion}` : 'Arus Kas'} · {monthLabel}
      </div>
    </div>
  );
}
